import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { isGuest } from '../lib/guestMode';
import GuestEmptyState from '../components/GuestEmptyState';
import { useUI } from '../context/UIContext';
import { useTutorial, TUTORIAL_STEPS } from '../context/TutorialContext';
import { useProfile } from '../context/ProfileContext';
import { useTheme } from '../context/ThemeContext';
import TutorialOverlay, { TutorialBackdrop } from '../components/TutorialOverlay';
import { supabase } from '../lib/supabase';
import { sendNewFollowerNotification, sendCardRemovedFromTradeNotification } from '../lib/notifications';
import { setCardNavigationContext } from '../lib/cardNavigationContext';
import { getBulkCardPrices, isWithinCooldown, daysSinceRefresh,
         refreshCollectionPrices, autoFetchMissingPrices, formatPrice, isGradedCard } from '../services/cardPriceCache';

// Modals
import AddCardModal from '../components/modals/AddCardModal';
import AddBinderModal from '../components/modals/AddBinderModal';
import AddToBinderModal from '../components/modals/AddToBinderModal';
// Old scanner modals moved to _old-scanner-files/ - now using /scanner page
import SortFilterModal from '../components/modals/SortFilterModal';
import WishListSearchModal from '../components/modals/WishListSearchModal';
import WishListMatchModal from '../components/modals/WishListMatchModal';
import WishListCardMenu from '../components/modals/WishListCardMenu';
import AllBadgesModal from '../components/modals/AllBadgesModal';
import { BadgeProgressRing } from '../components/BadgeProgressRing';

// Binder Images
import BabyBlueBinder from '../assets/images/Binders/BabyBlueBinder.png';
import BlueBinder from '../assets/images/Binders/BlueBinder.png';
import GreenBinder from '../assets/images/Binders/GreenBinder.png';
import MintGreenBinder from '../assets/images/Binders/MintGreenBinder.png';
import OrangeBinder from '../assets/images/Binders/OrangeBinder.png';
import PinkBinder from '../assets/images/Binders/PinkBinder.png';
import PurpleBinder from '../assets/images/Binders/PurpleBinder.png';
import RedBinder from '../assets/images/Binders/RedBinder.png';
import WhiteBinder from '../assets/images/Binders/WhiteBinder.png';
import YellowBinder from '../assets/images/Binders/YellowBinder.png';
import CardImage from '../components/CardImage';

const binderImages = {
  babyblue: BabyBlueBinder,
  blue: BlueBinder,
  green: GreenBinder,
  mintgreen: MintGreenBinder,
  orange: OrangeBinder,
  pink: PinkBinder,
  purple: PurpleBinder,
  red: RedBinder,
  white: WhiteBinder,
  yellow: YellowBinder,
};

// Icons
const ActivityIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
  </svg>
);

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const CardsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
    <path d="M9 21V9" />
  </svg>
);

const BindersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    <path d="M8 7h8" />
    <path d="M8 11h6" />
  </svg>
);

const WishListIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const MatchBoltIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

// Grid icon for Sets view toggle
const SetsViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

// Cards list icon for Cards view toggle
const CardsViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
    <path d="M3 15h18" />
    <path d="M9 3v18" />
  </svg>
);

const SortIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 16 4 4 4-4" />
    <path d="M7 20V4" />
    <path d="m21 8-4-4-4 4" />
    <path d="M17 4v16" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    <path d="m15 5 4 4" />
  </svg>
);

const ZoomInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
    <path d="M11 8v6" />
    <path d="M8 11h6" />
  </svg>
);

const ZoomOutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
    <path d="M8 11h6" />
  </svg>
);

const PlusIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const StarIcon = ({ filled }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? "#facc15" : "none"} stroke="#facc15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const MoreVerticalIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="1.5"></circle>
    <circle cx="12" cy="12" r="1.5"></circle>
    <circle cx="12" cy="19" r="1.5"></circle>
  </svg>
);

const LockIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M19 11H5a2 2 0 00-2 2v7a2 2 0 002 2h14a2 2 0 002-2v-7a2 2 0 00-2-2z"/>
    <path d="M7 11V7a5 5 0 0110 0v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// User Options Menu Component
const UserOptionsMenu = ({ isOpen, onClose, isFollowing, onToggleFollow, onReport, onBlock }) => {
  if (!isOpen) return null;

  return (
    <>
      <div style={menuStyles.backdrop} onClick={onClose} />
      <div style={menuStyles.menu}>
        <button style={menuStyles.menuItem} onClick={() => { onToggleFollow(); onClose(); }}>
          <span style={menuStyles.menuItemText}>
            {isFollowing ? 'Unfollow' : 'Follow'}
          </span>
        </button>
        <div style={menuStyles.menuDivider} />
        <button style={menuStyles.menuItem} onClick={() => { onBlock(); onClose(); }}>
          <span style={menuStyles.menuItemText}>Block User</span>
        </button>
        <div style={menuStyles.menuDivider} />
        <button style={menuStyles.menuItemDanger} onClick={() => { onReport(); onClose(); }}>
          <span style={menuStyles.menuItemTextDanger}>Report User</span>
        </button>
      </div>
    </>
  );
};

// Report User Modal Component
const ReportUserModal = ({ isOpen, onClose, userId, currentUserId, onSuccess }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reasons = [
    { id: 'nsfw', label: 'NSFW Content', description: 'Posts adult or inappropriate content' },
    { id: 'wrong_content', label: 'Wrong Content', description: 'Posts non-Pokemon/TCG content' },
    { id: 'spam', label: 'Spam', description: 'Posts repetitive or promotional content' },
    { id: 'scam', label: 'Scam', description: 'Fraudulent or deceptive behavior' },
    { id: 'other', label: 'Other', description: 'Another issue not listed' },
  ];

  const handleSubmit = async () => {
    if (!selectedReason || !currentUserId || !userId) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('reports')
        .insert({
          reporter_id: currentUserId,
          reported_user_id: userId,
          reason: selectedReason,
          description: description.trim() || null,
          status: 'pending',
        });

      if (error) {
        if (error.code === '23505') {
          alert('You have already reported this user.');
        } else {
          throw error;
        }
      } else {
        onSuccess && onSuccess();
        onClose();
        setSelectedReason(null);
        setDescription('');
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      alert('Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={reportStyles.overlay} onClick={onClose}>
      <div style={reportStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={reportStyles.header}>
          <h2 style={reportStyles.title}>Report User</h2>
          <button style={reportStyles.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div style={reportStyles.content}>
          <p style={reportStyles.subtitle}>Why are you reporting this user?</p>

          <div style={reportStyles.reasonsList}>
            {reasons.map((reason) => (
              <button
                key={reason.id}
                style={{
                  ...reportStyles.reasonButton,
                  ...(selectedReason === reason.id ? reportStyles.reasonButtonSelected : {}),
                }}
                onClick={() => setSelectedReason(reason.id)}
              >
                <div style={reportStyles.reasonContent}>
                  <span style={{
                    ...reportStyles.reasonLabel,
                    ...(selectedReason === reason.id ? reportStyles.reasonLabelSelected : {}),
                  }}>{reason.label}</span>
                  <span style={reportStyles.reasonDescription}>{reason.description}</span>
                </div>
                <div style={{
                  ...reportStyles.radioOuter,
                  ...(selectedReason === reason.id ? reportStyles.radioOuterSelected : {}),
                }}>
                  {selectedReason === reason.id && <div style={reportStyles.radioInner} />}
                </div>
              </button>
            ))}
          </div>

          <div style={reportStyles.descriptionSection}>
            <label style={reportStyles.descriptionLabel}>Additional details (optional)</label>
            <textarea
              style={reportStyles.descriptionInput}
              placeholder="Tell us more about the issue..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <div style={reportStyles.footer}>
          <button style={reportStyles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{
              ...reportStyles.submitButton,
              ...((!selectedReason || submitting) ? reportStyles.submitButtonDisabled : {}),
            }}
            onClick={handleSubmit}
            disabled={!selectedReason || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
};

const menuStyles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  menu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '4px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    overflow: 'hidden',
    minWidth: '140px',
    zIndex: 1000,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
  },
  menuItem: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  menuItemDanger: {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  },
  menuItemText: {
    color: 'var(--text)',
    fontSize: '14px',
    fontWeight: '500',
  },
  menuItemTextDanger: {
    color: 'var(--error)',
    fontSize: '14px',
    fontWeight: '500',
  },
  menuDivider: {
    height: '1px',
    backgroundColor: 'var(--border)',
  },
};

const reportStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--overlay)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '20px',
  },
  modal: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: 'var(--bg-sheet)',
    borderRadius: '16px',
    border: '1px solid var(--border)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border)',
  },
  title: {
    color: 'var(--text)',
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  closeButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: 'var(--bg-card)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
  },
  content: {
    padding: '20px',
  },
  subtitle: {
    color: 'var(--text-secondary)',
    fontSize: '14px',
    margin: '0 0 16px 0',
  },
  reasonsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  reasonButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    textAlign: 'left',
  },
  reasonButtonSelected: {
    backgroundColor: 'var(--accent-bg)',
    borderColor: 'var(--accent)',
  },
  reasonContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  reasonLabel: {
    color: 'var(--text)',
    fontSize: '14px',
    fontWeight: '500',
  },
  reasonLabelSelected: {
    color: 'var(--accent)',
  },
  reasonDescription: {
    color: 'var(--text-secondary)',
    fontSize: '12px',
  },
  radioOuter: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioOuterSelected: {
    borderColor: 'var(--accent)',
  },
  radioInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: 'var(--accent)',
  },
  descriptionSection: {
    marginTop: '20px',
  },
  descriptionLabel: {
    display: 'block',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    marginBottom: '8px',
  },
  descriptionInput: {
    width: '100%',
    padding: '12px',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    color: 'var(--text)',
    fontSize: '14px',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  footer: {
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid var(--border)',
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    color: 'var(--text-secondary)',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'var(--error)',
    border: 'none',
    borderRadius: '10px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButtonDisabled: {
    backgroundColor: 'var(--border)',
    cursor: 'not-allowed',
  },
};

const Collection = () => {
  const { user } = useAuth();
  const { activeProfile, userProfile } = useProfile();
  const { theme, isDark } = useTheme();
  const { addAction, setAddAction } = useUI();
  const {
    currentStep,
    handleAddCardBinderComplete,
    handleCardAdded,
    handleBinderAdded,
    handleCongratsComplete,
    handleMoreTutorials,
  } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId: viewUserId } = useParams(); // If viewing another user's collection
  const [searchParams, setSearchParams] = useSearchParams();
  const binderParam = searchParams.get('binder'); // If coming from Full Binder link
  const tabParam = searchParams.get('tab'); // Current tab
  const setParam = searchParams.get('set'); // Wish list set
  const cardSetParam = searchParams.get('cardset'); // Cards tab set filter
  const [profile, setProfile] = useState(null);
  const [isViewingOther, setIsViewingOther] = useState(false);
  const [activeTab, setActiveTab] = useState(
    tabParam || (binderParam ? 'binders' : 'cards')
  );
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  // Smart search state - combined to prevent multiple re-renders
  const [smartSearch, setSmartSearch] = useState({
    open: false,
    query: '',
    expandedSection: null, // Only one section open at a time
  });
  const [tcgSearchResults, setTcgSearchResults] = useState([]);
  const [tcgSearchLoading, setTcgSearchLoading] = useState(false);
  const searchInputRef = useRef(null);
  const loadMoreObserver = useRef(null);
  const [cards, setCards] = useState([]);
  const [binders, setBinders] = useState([]);
  const [selectedBinder, setSelectedBinder] = useState(null);
  // Preference defaults and storage key
  const PREFS_STORAGE_KEY = 'cardchase_collection_prefs';
  const DEFAULT_PREFS = {
    cardsViewMode: 'cards',
    cardZoom: 0,
    cardsSetsZoom: 1,
    cardsSetsSort: 'newest',
    cardsSetsFilter: 'all',
    setsLanguage: 'en', // 'en' | 'ja' | 'zh-tw' | etc.
    setCardsSort: 'number-asc',
    tradeFilter: 'all',
    visibilityFilter: 'all',
    cardSortBy: 'recent',
    binderSortBy: 'name-asc',
    wishListZoom: 1,
  };

  // Load saved preferences from localStorage
  const getSavedPrefs = () => {
    try {
      const saved = localStorage.getItem(PREFS_STORAGE_KEY);
      if (saved) {
        return { ...DEFAULT_PREFS, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error('Error loading preferences:', e);
    }
    return DEFAULT_PREFS;
  };

  const savedPrefs = getSavedPrefs();

  const [cardZoom, setCardZoom] = useState(savedPrefs.cardZoom); // 0 = small, 1 = medium (name), 2 = large (name + grade)
  const [cardsViewMode, setCardsViewMode] = useState(savedPrefs.cardsViewMode); // 'cards' | 'sets'
  const [cardsSetsZoom, setCardsSetsZoom] = useState(savedPrefs.cardsSetsZoom); // 0 = small, 1 = medium, 2 = large for sets view
  const [cardsSetsSort, setCardsSetsSort] = useState(savedPrefs.cardsSetsSort); // 'newest' | 'oldest' | 'a-z' | 'z-a'
  const [cardsSetsFilter, setCardsSetsFilter] = useState(savedPrefs.cardsSetsFilter); // 'all' | 'owned'
  const [setsLanguage, setSetsLanguage] = useState(savedPrefs.setsLanguage); // 'en' | 'ja' | 'zh-tw' | etc.
  const [setCardsSort, setSetCardsSort] = useState(savedPrefs.setCardsSort); // 'number-asc' | 'number-desc' | 'name-asc' | 'name-desc'
  const [selectedCardSet, setSelectedCardSet] = useState(cardSetParam || null); // Set ID when drilling into a set from sets view
  const [showStatsPanel, setShowStatsPanel] = useState(false);
  const [cardPrices, setCardPrices] = useState({}); // { cardId: { price, formatted } }
  const [refreshing, setRefreshing] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState({ done: 0, total: 0 });
  const [lastCollectionRefresh, setLastCollectionRefresh] = useState(null);
  const [autoFetching, setAutoFetching] = useState(false);
  const refreshCooldown = lastCollectionRefresh && isWithinCooldown(lastCollectionRefresh);
  const pricingEnabled = activeProfile?.pricing_trigger && activeProfile?.pricing_enabled === true;
  const [tradeStats, setTradeStats] = useState({
    totalTrades: 0,
    averageRating: 0,
    totalReviews: 0,
    ratingBreakdown: {
      timeliness: 0,
      communication: 0,
      fairness: 0,
      overall: 0,
    },
    // Trade funnel stats
    messagesStarted: 0,
    scheduled: 0,
    completed: 0,
    incomplete: 0,
    // Activity indicators
    avgResponseTime: null, // in hours
    lastActive: null, // timestamp
    completionRate: 0, // percentage
  });
  const [currentUserId, setCurrentUserId] = useState(null);
  const [distanceToUser, setDistanceToUser] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // Modal states
  const [allBadgesOpen, setAllBadgesOpen] = useState(false);
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const [addBinderModalOpen, setAddBinderModalOpen] = useState(false);
  const [addToBinderModalOpen, setAddToBinderModalOpen] = useState(false);
  // Old scan modals removed - now using navigate('/scanner')
  const [sortFilterModalOpen, setSortFilterModalOpen] = useState(false);
  const [wishListSearchOpen, setWishListSearchOpen] = useState(false);
  const [wishListCount, setWishListCount] = useState(0);
  const [wishListItems, setWishListItems] = useState([]);
  const [wishListLoading, setWishListLoading] = useState(false);
  const [selectedWishListSet, setSelectedWishListSet] = useState(setParam || null); // null = sets view, 'all' = all cards, or set_id
  const [wishListZoom, setWishListZoom] = useState(savedPrefs.wishListZoom); // 0 = small, 1 = medium, 2 = large
  const [wishListMatches, setWishListMatches] = useState([]); // Cards they have that YOU want
  const [reverseMatches, setReverseMatches] = useState([]); // Cards YOU have that THEY want
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [matchCheckTrigger, setMatchCheckTrigger] = useState(0); // Increment to force re-check
  const [allSets, setAllSets] = useState([]);
  const [allSetsLoading, setAllSetsLoading] = useState(false);
  const [setsDisplayLimit, setSetsDisplayLimit] = useState(30);
  const [cardsDisplayLimit, setCardsDisplayLimit] = useState(30);
  const [wishDisplayLimit, setWishDisplayLimit] = useState(30);
  const [tcgSetCards, setTcgSetCards] = useState([]); // Cards for selected set from TCGdex
  const [tcgSetCardsLoading, setTcgSetCardsLoading] = useState(false);
  const [setCardFilter, setSetCardFilter] = useState('all'); // 'all' | 'owned' | 'wish'
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [bulkEditMode, setBulkEditMode] = useState(null); // 'tradeable' | 'pc' | 'private' | 'public' | 'delete'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedCards, setSelectedCards] = useState([]); // Card IDs selected for bulk edit

  // Sort/Filter state (persisted)
  const [tradeFilter, setTradeFilter] = useState(savedPrefs.tradeFilter); // 'all' | 'pc' | 'tradable'
  const [visibilityFilter, setVisibilityFilter] = useState(savedPrefs.visibilityFilter); // 'all' | 'public' | 'private'
  // imageFilter reads from global preference (shared with Home, Preferences, Profile)
  const [imageFilter, setImageFilter] = useState(() => {
    return localStorage.getItem('imagePreference') === 'photo' ? 'photo' : 'digital';
  }); // 'digital' | 'photo'
  const [cardSortBy, setCardSortBy] = useState(savedPrefs.cardSortBy); // 'name-asc' | 'name-desc' | 'type' | 'recent' | 'hp'
  const [binderSortBy, setBinderSortBy] = useState(savedPrefs.binderSortBy); // 'name-asc' | 'name-desc'
  const [wishListSortBy, setWishListSortBy] = useState('newest'); // 'newest' | 'oldest' | 'name-asc' | 'name-desc' | 'recent'

  // Wish list card menu state
  const [wishCardMenuOpen, setWishCardMenuOpen] = useState(false);
  const [selectedWishCard, setSelectedWishCard] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState('info');

  // Toast helper
  const showToast = useCallback((message, type = 'info') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(null), 3000);
  }, []);

  // Save preferences to localStorage when they change
  useEffect(() => {
    const prefs = {
      cardsViewMode,
      cardZoom,
      cardsSetsZoom,
      cardsSetsSort,
      cardsSetsFilter,
      setsLanguage,
      setCardsSort,
      tradeFilter,
      visibilityFilter,
      cardSortBy,
      binderSortBy,
      wishListZoom,
    };
    try {
      localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
    } catch (e) {
      console.error('Error saving preferences:', e);
    }
  }, [cardsViewMode, cardZoom, cardsSetsZoom, cardsSetsSort, cardsSetsFilter, setsLanguage, setCardsSort, tradeFilter, visibilityFilter, cardSortBy, binderSortBy, wishListZoom]);

  // Sync imageFilter with global preference and save when changed
  useEffect(() => {
    localStorage.setItem('imagePreference', imageFilter);
  }, [imageFilter]);

  // Sync when returning to page (preference may have changed in Preferences screen)
  useEffect(() => {
    const syncImagePreference = () => {
      const pref = localStorage.getItem('imagePreference');
      setImageFilter(pref === 'photo' ? 'photo' : 'digital');
    };
    window.addEventListener('focus', syncImagePreference);
    return () => window.removeEventListener('focus', syncImagePreference);
  }, []);

  // Reset preferences to defaults
  const resetPreferences = useCallback(() => {
    setCardsViewMode(DEFAULT_PREFS.cardsViewMode);
    setCardZoom(DEFAULT_PREFS.cardZoom);
    setCardsSetsZoom(DEFAULT_PREFS.cardsSetsZoom);
    setCardsSetsSort(DEFAULT_PREFS.cardsSetsSort);
    setCardsSetsFilter(DEFAULT_PREFS.cardsSetsFilter);
    setSetsLanguage(DEFAULT_PREFS.setsLanguage);
    setSetCardsSort(DEFAULT_PREFS.setCardsSort);
    setTradeFilter(DEFAULT_PREFS.tradeFilter);
    setVisibilityFilter(DEFAULT_PREFS.visibilityFilter);
    setImageFilter('digital'); // Reset to digital (default)
    setCardSortBy(DEFAULT_PREFS.cardSortBy);
    setBinderSortBy(DEFAULT_PREFS.binderSortBy);
    setWishListZoom(DEFAULT_PREFS.wishListZoom);
    showToast('Preferences reset to defaults');
  }, [showToast]);

  // Get themed styles
  const styles = getStyles(theme);

  // Sync URL params with state
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    // Update tab param
    if (activeTab && activeTab !== 'cards') {
      params.set('tab', activeTab);
    } else {
      params.delete('tab');
    }

    // Update set param for wish list
    if (activeTab === 'wishlist' && selectedWishListSet) {
      params.set('set', selectedWishListSet);
    } else {
      params.delete('set');
    }

    // Update cardset param for cards tab set filter
    if (activeTab === 'cards' && selectedCardSet) {
      params.set('cardset', selectedCardSet);
    } else {
      params.delete('cardset');
    }

    // Keep binder param if exists
    if (binderParam) {
      params.set('binder', binderParam);
    }

    // Update URL - push to history for back button support
    const newSearch = params.toString();
    const currentSearch = searchParams.toString();
    if (newSearch !== currentSearch) {
      setSearchParams(params);
    }
  }, [activeTab, selectedWishListSet, selectedCardSet]);

  // Listen for URL param changes (back/forward buttons)
  useEffect(() => {
    const newTab = searchParams.get('tab') || 'cards';
    const newSet = searchParams.get('set') || null;
    const newCardSet = searchParams.get('cardset') || null;

    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
    if (newSet !== selectedWishListSet) {
      setSelectedWishListSet(newSet);
    }
    if (newCardSet !== selectedCardSet) {
      setSelectedCardSet(newCardSet);
    }
  }, [searchParams]);

  // Fetch user profile and data
  useEffect(() => {
    const fetchData = async () => {
      // Guests viewing another user's collection: skip auth, go straight to fetch
      const isGuestViewingOther = !user && isGuest() && viewUserId;
      if ((!user || !activeProfile) && !isGuestViewingOther) {
        console.log('[Collection] fetchData early return:', { user: !!user, activeProfile: !!activeProfile });
        setInitialLoading(false);
        return;
      }

      // Use activeProfile.id (kid's ID when parent views as kid)
      if (activeProfile?.id) setCurrentUserId(activeProfile.id);

      let profileData;

      // Check if viewing another user's collection
      if (viewUserId) {
        // Fetch the viewed user's profile by their user id
        const { data } = await supabase
          .from('users')
          .select('id, username, avg_rating, total_ratings, successful_trades, successful_reports, image_contributions, auth_id, latitude, longitude, location_sharing_enabled')
          .eq('id', viewUserId)
          .single();
        profileData = data;
        // Check if this is "our own" collection (either the active profile)
        setIsViewingOther(data?.id !== activeProfile?.id);

        // Calculate distance to this user (only if signed in with a profile)
        if (data && activeProfile?.id && data.id !== activeProfile.id && data.latitude && data.longitude && data.location_sharing_enabled && activeProfile.latitude && activeProfile.longitude) {
          const R = 3959;
          const dLat = (data.latitude - activeProfile.latitude) * Math.PI / 180;
          const dLon = (data.longitude - activeProfile.longitude) * Math.PI / 180;
          const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(activeProfile.latitude * Math.PI / 180) * Math.cos(data.latitude * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
          setDistanceToUser(Math.round(R * c));
        }

        // Check if current user is following this user (only if signed in)
        if (data && activeProfile?.id && data.id !== activeProfile.id) {
          const { data: followData } = await supabase
            .from('user_follows')
            .select('id')
            .eq('follower_id', activeProfile.id)
            .eq('following_id', viewUserId)
            .single();
          setIsFollowing(!!followData);
        }
      } else {
        // Use activeProfile directly (kid's profile when parent views as kid)
        profileData = activeProfile;
        setIsViewingOther(false);
      }

      if (profileData) {
        setProfile(profileData);

        // Fetch cards (exclude soft-deleted)
        const { data: cardsData, error: cardsError } = await supabase
          .from('cards')
          .select('*')
          .eq('user_id', profileData.id)
          .is('deleted_at', null)
          .order('created_at', { ascending: false });

        console.log('[Collection] Cards fetch:', { count: cardsData?.length, error: cardsError, userId: profileData.id });
        if (cardsData) setCards(cardsData);

        // Fetch binders
        const { data: bindersData } = await supabase
          .from('binders')
          .select('*')
          .eq('user_id', profileData.id)
          .order('created_at', { ascending: false });

        if (bindersData) {
          setBinders(bindersData);

          // If coming from Full Binder link, auto-select the binder immediately
          if (binderParam) {
            const targetBinder = bindersData.find(b => b.id === binderParam);
            if (targetBinder) {
              setSelectedBinder(targetBinder);
            }
          }
        }

        // Fetch wish list (for own profile or viewing others)
        setWishListLoading(true);
        const { data: wishData, count } = await supabase
          .from('wish_list')
          .select('*', { count: 'exact' })
          .eq('user_id', profileData.id)
          .order('added_at', { ascending: false });
        setWishListItems(wishData || []);
        setWishListCount(count || 0);
        setWishListLoading(false);

        // Fetch detailed ratings from trade_reviews table
        const { data: ratingsData } = await supabase
          .from('trade_reviews')
          .select('communication_rating, fairness_rating, timeliness_rating, overall_rating')
          .eq('reviewed_user_id', profileData.id);

        // Calculate rating breakdown from trade reviews
        let ratingBreakdown = { timeliness: 0, communication: 0, fairness: 0, overall: 0 };

        if (ratingsData && ratingsData.length > 0) {
          let totalTimeliness = 0, totalComm = 0, totalFairness = 0, totalOverall = 0;
          let countTimeliness = 0, countComm = 0, countFairness = 0, countOverall = 0;

          ratingsData.forEach(rating => {
            if (rating.timeliness_rating) { totalTimeliness += rating.timeliness_rating; countTimeliness++; }
            if (rating.communication_rating) { totalComm += rating.communication_rating; countComm++; }
            if (rating.fairness_rating) { totalFairness += rating.fairness_rating; countFairness++; }
            if (rating.overall_rating) { totalOverall += rating.overall_rating; countOverall++; }
          });

          ratingBreakdown = {
            timeliness: countTimeliness > 0 ? totalTimeliness / countTimeliness : 0,
            communication: countComm > 0 ? totalComm / countComm : 0,
            fairness: countFairness > 0 ? totalFairness / countFairness : 0,
            overall: countOverall > 0 ? totalOverall / countOverall : 0,
          };
        }

        // Fetch trade activity stats
        const [
          messagesStartedResult,
          scheduledResult,
          completedResult,
          incompleteResult,
          lastMessageResult,
        ] = await Promise.all([
          // Messages started = accepted trade requests
          supabase
            .from('trade_requests')
            .select('id', { count: 'exact', head: true })
            .or(`requester_id.eq.${profileData.id},card_owner_id.eq.${profileData.id}`)
            .eq('status', 'accepted'),

          // Get user's trade IDs for scheduled count
          (async () => {
            const { data: userTrades } = await supabase
              .from('trade_requests')
              .select('id')
              .or(`requester_id.eq.${profileData.id},card_owner_id.eq.${profileData.id}`);

            if (!userTrades || userTrades.length === 0) return { count: 0 };

            const tradeIds = userTrades.map(t => t.id);
            return supabase
              .from('trade_schedules')
              .select('id', { count: 'exact', head: true })
              .in('trade_id', tradeIds)
              .eq('status', 'confirmed');
          })(),

          // Completed meetups
          supabase
            .from('meetups')
            .select('id', { count: 'exact', head: true })
            .or(`requester_id.eq.${profileData.id},receiver_id.eq.${profileData.id}`)
            .eq('status', 'completed'),

          // Incomplete meetups
          supabase
            .from('meetups')
            .select('id', { count: 'exact', head: true })
            .or(`requester_id.eq.${profileData.id},receiver_id.eq.${profileData.id}`)
            .eq('status', 'incomplete'),

          // Last message sent (for activity indicator)
          supabase
            .from('messages')
            .select('created_at')
            .eq('sender_id', profileData.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single(),
        ]);

        const messagesStarted = messagesStartedResult?.count || 0;
        const scheduled = scheduledResult?.count || 0;
        const completed = completedResult?.count || 0;
        const incomplete = incompleteResult?.count || 0;
        const lastActive = lastMessageResult?.data?.created_at || null;

        // Calculate average response time
        let avgResponseTime = null;

        // Get trade IDs for response time calculation
        const { data: userTradesForResponse } = await supabase
          .from('trade_requests')
          .select('id')
          .or(`requester_id.eq.${profileData.id},card_owner_id.eq.${profileData.id}`)
          .eq('status', 'accepted');

        if (userTradesForResponse && userTradesForResponse.length > 0) {
          const tradeIds = userTradesForResponse.map(t => t.id);

          const { data: responseMessages } = await supabase
            .from('messages')
            .select('sender_id, created_at, trade_request_id')
            .in('trade_request_id', tradeIds)
            .order('created_at', { ascending: true })
            .limit(200);

          const responseTimeResult = responseMessages || [];

          if (responseTimeResult.length > 1) {
            // Group messages by trade
            const messagesByTrade = {};
            responseTimeResult.forEach(msg => {
              if (!messagesByTrade[msg.trade_request_id]) {
                messagesByTrade[msg.trade_request_id] = [];
              }
              messagesByTrade[msg.trade_request_id].push(msg);
            });

            // Calculate response times for this user
            const responseTimes = [];
            Object.values(messagesByTrade).forEach(tradeMessages => {
              for (let i = 1; i < tradeMessages.length; i++) {
                const prevMsg = tradeMessages[i - 1];
                const currMsg = tradeMessages[i];

                // If prev message is from someone else and current is from this user = response
                if (prevMsg.sender_id !== profileData.id && currMsg.sender_id === profileData.id) {
                  const responseTime = new Date(currMsg.created_at) - new Date(prevMsg.created_at);
                  // Only count reasonable response times (under 72 hours)
                  if (responseTime > 0 && responseTime < 72 * 60 * 60 * 1000) {
                    responseTimes.push(responseTime);
                  }
                }
              }
            });

            if (responseTimes.length > 0) {
              const avgMs = responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
              avgResponseTime = avgMs / (1000 * 60 * 60); // Convert to hours
            }
          }
        }

        // Calculate completion rate (scheduled to completed)
        const cancelled = profileData.trades_cancelled || 0;
        const cancelledLastMinute = profileData.trades_cancelled_last_minute || 0;
        const completionRate = (completed + cancelled) > 0
          ? Math.round((completed / (completed + cancelled)) * 100)
          : 0;

        // Calculate average rating from reviews data (use overall rating average)
        const calculatedAvgRating = ratingBreakdown.overall || profileData.avg_rating || profileData.average_rating || 0;

        setTradeStats({
          totalTrades: profileData.successful_trades || profileData.completed_trades_count || 0,
          averageRating: calculatedAvgRating,
          totalReviews: ratingsData?.length || profileData.total_ratings || 0,
          ratingBreakdown,
          messagesStarted,
          scheduled,
          completed,
          incomplete,
          cancelled,
          cancelledLastMinute,
          avgResponseTime,
          lastActive,
          completionRate,
        });
      }

      setInitialLoading(false);
    };

    fetchData();
  }, [user, activeProfile?.id, viewUserId, binderParam]);

  // When viewing another user, reset view to show their cards directly
  useEffect(() => {
    if (viewUserId) {
      setCardsViewMode('cards');
      setTradeFilter('all');
      setVisibilityFilter('all');
      setCardsDisplayLimit(30);
      setSearchQuery('');
    }
  }, [viewUserId]);

  // Fetch prices from card_prices cache table
  useEffect(() => {
    if (!cards.length || !pricingEnabled) return;

    let cancelled = false;

    const fetchPrices = async () => {
      try {
      // Build lookup list
      const cardLookups = cards.map(card => ({
        id: card.id,
        name: card.english_name || card.name,
        set_name: card.set_name || '',
        set_number: card.set_number || card.card_id?.split('-').pop() || '',
        grade: card.grade || 'NM',
        variant: card.variant || '',
      }));

      console.log('[Collection] Reading prices for', cardLookups.length, 'cards from card_prices table');

      // Bulk read from card_prices
      const cachedResults = await getBulkCardPrices(cardLookups);
      if (cancelled) return;

      console.log('[Collection] Got', cachedResults.size, 'cached prices out of', cardLookups.length, 'cards');

      // Apply prices (skip null/zero prices so badge doesn't show $0.00)
      const prices = {};
      let priceCount = 0;
      for (const [cardId, result] of cachedResults) {
        if (result.price != null && result.price > 0) {
          prices[cardId] = { price: result.price, formatted: result.formatted };
          priceCount++;
        }
      }
      setCardPrices(prices);
      console.log('[Collection] Applied', priceCount, 'prices to UI');

      // Only set cooldown if majority of cards have cached prices (full refresh was done)
      // Don't lock the button from stray individual card caches
      const cachedRatio = cachedResults.size / cardLookups.length;
      if (cachedRatio > 0.5) {
        let oldestRefresh = null;
        for (const [, result] of cachedResults) {
          const ts = result.row?.last_refreshed_at;
          if (ts && (!oldestRefresh || new Date(ts) < new Date(oldestRefresh))) {
            oldestRefresh = ts;
          }
        }
        if (oldestRefresh) {
          setLastCollectionRefresh(oldestRefresh);
          console.log('[Collection] Cooldown set from oldest refresh:', oldestRefresh);
        }
      } else {
        console.log('[Collection] Only', Math.round(cachedRatio * 100) + '% cached, refresh button stays enabled');
      }

      // Auto-fetch missing prices (own collection + other users)
      if (!cancelled) {
        const missing = cardLookups.filter(c => !cachedResults.has(c.id));
        if (missing.length > 0) {
          console.log('[Collection] Auto-fetching', missing.length, 'missing prices via Countdown');
          setAutoFetching(true);
          await autoFetchMissingPrices(missing, (done, total) => {
            if (!cancelled) setRefreshProgress({ done, total });
          });
          if (!cancelled) {
            // Re-read all prices after auto-fetch
            const updated = await getBulkCardPrices(cardLookups);
            const updatedPrices = {};
            for (const [cardId, result] of updated) {
              if (result.price != null && result.price > 0) {
                updatedPrices[cardId] = { price: result.price, formatted: result.formatted };
              }
            }
            setCardPrices(updatedPrices);
            setAutoFetching(false);
          }
        }
      }
      } catch (err) {
        console.error('[Collection] Price fetch error:', err);
        setAutoFetching(false);
      }
    };

    fetchPrices();
    return () => { cancelled = true; };
  }, [cards, pricingEnabled, isViewingOther]);

  // Total collection value
  const totalCollectionValue = useMemo(() => {
    const total = Object.values(cardPrices).reduce((sum, p) => sum + (p.price || 0), 0);
    return total > 0 ? formatPrice(total) : null;
  }, [cardPrices]);

  // Badge data for progress rings
  const badgeData = useMemo(() => {
    const qualifyingBinders = binders.filter(b => {
      const binderCardCount = cards.filter(c => c.binder_id === b.id).length;
      return binderCardCount >= 10;
    }).length;

    return {
      completedTrades: Math.max(tradeStats.completed || 0, profile?.successful_trades || 0),
      successfulReports: profile?.successful_reports || 0,
      imageContributions: profile?.image_contributions || 0,
      totalCards: cards.length,
      qualifyingBinders,
      charterMember: profile?.charter_member ? 1 : 0,
    };
  }, [tradeStats.completed, profile, cards, binders]);

  // Check collector/organized badges when counts change
  useEffect(() => {
    if (!isViewingOther && badgeData.totalCards > 0) {
      window.dispatchEvent(new CustomEvent('badge-check', {
        detail: { badgeType: 'collector', count: badgeData.totalCards },
      }));
    }
    if (!isViewingOther && badgeData.qualifyingBinders > 0) {
      window.dispatchEvent(new CustomEvent('badge-check', {
        detail: { badgeType: 'organized', count: badgeData.qualifyingBinders },
      }));
    }
  }, [badgeData.totalCards, badgeData.qualifyingBinders, isViewingOther]);

  // Handle add action from bottom nav
  useEffect(() => {
    if (addAction === 'card') {
      navigate('/bulk-scanner');
      setAddAction(null);
    } else if (addAction === 'binder') {
      setAddBinderModalOpen(true);
      setAddAction(null);
    }
  }, [addAction, setAddAction, navigate]);


  // Detect wish list matches when viewing another user's collection
  // Checks BOTH directions: cards they have that you want, AND cards you have that they want
  useEffect(() => {
    const detectMatches = async () => {
      // Only run when viewing someone else's collection
      if (!isViewingOther || !viewUserId || !activeProfile?.id) {
        setWishListMatches([]);
        setReverseMatches([]);
        return;
      }

      try {
        // === Direction 1: Cards THEY have that YOU want ===
        // Get my full wish list (all items, not just priority)
        const { data: myWishList } = await supabase
          .from('wish_list')
          .select('tcgdex_card_id, variant')
          .eq('user_id', activeProfile.id);

        let cardsTheyHaveThatIWant = [];
        if (myWishList && myWishList.length > 0) {
          const myWishListCardIds = myWishList.map(w => w.tcgdex_card_id).filter(Boolean);

          // Find matching cards in the other user's collection (any trading status)
          const { data: matchingCards } = await supabase
            .from('cards')
            .select('*')
            .eq('user_id', viewUserId)
            .is('deleted_at', null)
            .in('card_id', myWishListCardIds);

          if (matchingCards && matchingCards.length > 0) {
            cardsTheyHaveThatIWant = matchingCards.map(card => {
              const wishItem = myWishList.find(w => w.tcgdex_card_id === card.card_id);
              return {
                ...card,
                wishListVariant: wishItem?.variant || 'Normal',
                matchType: 'theyHave'
              };
            });
          }
        }

        // === Direction 2: Cards YOU have that THEY want ===
        // Get their full wish list (all items, not just priority)
        const { data: theirWishList } = await supabase
          .from('wish_list')
          .select('tcgdex_card_id, variant')
          .eq('user_id', viewUserId);

        let cardsIHaveThatTheyWant = [];
        if (theirWishList && theirWishList.length > 0) {
          const theirWishListCardIds = theirWishList.map(w => w.tcgdex_card_id).filter(Boolean);

          // Find matching cards in MY collection (any trading status)
          const { data: myMatchingCards } = await supabase
            .from('cards')
            .select('*')
            .eq('user_id', activeProfile.id)
            .is('deleted_at', null)
            .in('card_id', theirWishListCardIds);

          if (myMatchingCards && myMatchingCards.length > 0) {
            cardsIHaveThatTheyWant = myMatchingCards.map(card => {
              const wishItem = theirWishList.find(w => w.tcgdex_card_id === card.card_id);
              return {
                ...card,
                wishListVariant: wishItem?.variant || 'Normal',
                matchType: 'youHave'
              };
            });
          }
        }

        setWishListMatches(cardsTheyHaveThatIWant);
        setReverseMatches(cardsIHaveThatTheyWant);
      } catch (error) {
        console.error('Error detecting wish list matches:', error);
        setWishListMatches([]);
        setReverseMatches([]);
      }
    };

    detectMatches();
  }, [isViewingOther, viewUserId, activeProfile?.id, matchCheckTrigger]);

  // Re-check matches when navigating back to this page (pathname includes /collection or /user/)
  useEffect(() => {
    // This runs whenever location changes - if we're on a collection page, re-check
    if (isViewingOther && (location.pathname.includes('/collection') || location.pathname.includes('/user/'))) {
      setMatchCheckTrigger(prev => prev + 1);
    }
  }, [location.pathname, isViewingOther]);

  // Fetch all sets from TCGdex for wish list browse
  // TCGdex API supports server-side sorting by releaseDate
  // Track which language we last fetched
  const lastFetchedLanguageRef = useRef(null);

  useEffect(() => {
    const fetchAllSets = async () => {
      // Skip if already fetched for this language
      if (allSets.length > 0 && lastFetchedLanguageRef.current === setsLanguage) return;

      setAllSetsLoading(true);
      try {
        // Fetch sets sorted by releaseDate descending (newest first) from API
        // The API sorts correctly but doesn't return releaseDate in list response
        // So we fetch both orders and use index as sort key
        const response = await fetch(`https://api.tcgdex.net/v2/${setsLanguage}/sets?sort:field=releaseDate&sort:order=DESC`);
        if (response.ok) {
          const setsData = await response.json();
          // Add sortIndex based on API order (already sorted newest->oldest)
          const enrichedSets = setsData.map((set, index) => ({
            ...set,
            sortIndex: index // 0 = newest, higher = older
          }));
          setAllSets(enrichedSets);
          lastFetchedLanguageRef.current = setsLanguage;
        }
      } catch (error) {
        console.error('Error fetching sets:', error);
      }
      setAllSetsLoading(false);
    };

    fetchAllSets();
  }, [allSets.length, setsLanguage]);

  // Reset cards display limit when filters change
  useEffect(() => {
    setCardsDisplayLimit(30);
  }, [tradeFilter, visibilityFilter, cardSortBy, searchQuery]);

  // Reset wish display limit when sort changes
  useEffect(() => {
    setWishDisplayLimit(30);
  }, [wishListSortBy, searchQuery]);

  // Fetch cards for selected set from TCGdex (used by both wishlist and cards set view)
  useEffect(() => {
    const fetchSetCards = async () => {
      // Determine which set to fetch (wishlist set or cards set)
      const setToFetch = selectedWishListSet && selectedWishListSet !== 'all'
        ? selectedWishListSet
        : selectedCardSet;

      if (!setToFetch) {
        setTcgSetCards([]);
        return;
      }

      setTcgSetCardsLoading(true);
      try {
        const response = await fetch(`https://api.tcgdex.net/v2/${setsLanguage}/sets/${setToFetch}`);
        if (response.ok) {
          const setData = await response.json();
          setTcgSetCards(setData.cards || []);
        }
      } catch (error) {
        console.error('Error fetching set cards:', error);
        setTcgSetCards([]);
      }
      setTcgSetCardsLoading(false);
    };

    fetchSetCards();
  }, [selectedWishListSet, selectedCardSet, setsLanguage]);

  // Fetch TCGdex results when smart search is opened
  useEffect(() => {
    if (!smartSearch.open || !smartSearch.query || smartSearch.query.length < 2) {
      setTcgSearchResults([]);
      return;
    }

    const fetchResults = async () => {
      try {
        const response = await fetch(`https://api.tcgdex.net/v2/en/cards?name=${encodeURIComponent(smartSearch.query)}`);
        if (response.ok) {
          const results = await response.json();
          setTcgSearchResults(results.slice(0, 100));
        }
      } catch (error) {
        console.error('TCGdex search error:', error);
        setTcgSearchResults([]);
      }
      setTcgSearchLoading(false);
    };

    fetchResults();
  }, [smartSearch.open, smartSearch.query]);

  // Handle smart search trigger (Enter key or button) - single state update
  const triggerSmartSearch = useCallback(() => {
    if (searchQuery.length >= 2 && activeTab === 'cards') {
      setTcgSearchLoading(true);
      setSmartSearch({
        open: true,
        query: searchQuery,
        expandedSection: null,
      });
    }
  }, [searchQuery, activeTab]);

  // Toggle search section expansion - accordion style (only one open at a time)
  const toggleSearchSection = useCallback((section) => {
    setSmartSearch(prev => ({
      ...prev,
      expandedSection: prev.expandedSection === section ? null : section,
    }));
  }, []);

  // Toggle follow
  const handleToggleFollow = async () => {
    if (!currentUserId || !viewUserId) return;

    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);

    try {
      if (wasFollowing) {
        await supabase
          .from('user_follows')
          .delete()
          .eq('follower_id', currentUserId)
          .eq('following_id', viewUserId);
      } else {
        await supabase
          .from('user_follows')
          .insert({
            follower_id: currentUserId,
            following_id: viewUserId,
          });

        // Send notification to the user being followed
        const { data: followerData } = await supabase
          .from('users')
          .select('username')
          .eq('id', currentUserId)
          .single();
        const followerName = followerData?.username || 'Someone';
        sendNewFollowerNotification(viewUserId, followerName, currentUserId);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
      setIsFollowing(wasFollowing);
    }
  };

  // Block user
  const handleBlockUser = async () => {
    if (!currentUserId || !viewUserId) return;

    try {
      const { error } = await supabase
        .from('user_blocks')
        .insert({
          blocker_id: currentUserId,
          blocked_id: viewUserId,
        });

      if (error) {
        if (error.code === '23505') {
          alert('User is already blocked');
        } else {
          throw error;
        }
      } else {
        alert('User blocked. You will no longer see their content.');
        navigate(-1);
      }
    } catch (err) {
      console.error('Error blocking user:', err);
      alert('Failed to block user');
    }
  };

  // Report user
  const handleReportUser = () => {
    setReportModalOpen(true);
  };

  // Handle successful report
  const handleReportSuccess = () => {
    alert('Report submitted. Thank you for helping keep our community safe.');
  };

  const handleBinderClick = (binder) => {
    setSelectedBinder(binder);
  };

  const handleBackFromBinder = () => {
    setSelectedBinder(null);
  };

  // Apply filters and sorting to cards
  const getFilteredAndSortedCards = (cardList) => {
    let result = [...cardList];

    // Apply set filter (when drilling into a set from sets view)
    if (selectedCardSet) {
      result = result.filter(card => card.card_id?.startsWith(`${selectedCardSet}-`));
    }

    // Search filtering
    if (searchQuery && searchQuery.length >= 2) {
      const query = searchQuery.toLowerCase();
      result = result.filter(card =>
        card.card_name?.toLowerCase().includes(query) ||
        card.name?.toLowerCase().includes(query)
      );
    }

    // Apply trade filter (PC vs Tradable)
    if (tradeFilter === 'pc') {
      result = result.filter(card => card.trading_status !== 'TRADE');
    } else if (tradeFilter === 'tradable') {
      result = result.filter(card => card.trading_status === 'TRADE');
    }

    // Apply visibility filter (only for own collection)
    if (!isViewingOther) {
      if (visibilityFilter === 'public') {
        result = result.filter(card => !card.is_private);
      } else if (visibilityFilter === 'private') {
        result = result.filter(card => card.is_private);
      }
    }

    // imageFilter is a VIEW toggle (digital vs photo), not a filter
    // It determines which image URL to display, handled in the render

    // Apply sorting
    switch (cardSortBy) {
      case 'name-asc':
        result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'name-desc':
        result.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
        break;
      case 'type':
        result.sort((a, b) => {
          const typeA = a.types?.[0] || 'zzz';
          const typeB = b.types?.[0] || 'zzz';
          return typeA.localeCompare(typeB);
        });
        break;
      case 'recent':
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'hp':
        result.sort((a, b) => (b.hp || 0) - (a.hp || 0));
        break;
      default:
        break;
    }

    return result;
  };

  const filteredCards = getFilteredAndSortedCards(cards);
  console.log('[Collection] Render:', { totalCards: cards.length, filteredCards: filteredCards.length, displayLimit: cardsDisplayLimit });

  // Apply sorting to binders
  const getSortedBinders = () => {
    let result = [...binders];
    if (binderSortBy === 'name-asc') {
      result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (binderSortBy === 'name-desc') {
      result.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    }
    return result;
  };

  const sortedBinders = getSortedBinders();

  // Scroll-based infinite loading (replaces IntersectionObserver for iOS compatibility)
  const handleContentScroll = useCallback((e) => {
    const el = e.target;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    console.log('[Collection] Scroll:', { distFromBottom, scrollHeight: el.scrollHeight, scrollTop: el.scrollTop, clientHeight: el.clientHeight });
    if (distFromBottom < 300) {
      if (activeTab === 'cards' && cardsViewMode !== 'sets' && !selectedCardSet) {
        setCardsDisplayLimit(prev => {
          if (prev >= filteredCards.length) return prev;
          return Math.min(prev + 30, filteredCards.length);
        });
      } else if (activeTab === 'cards' && cardsViewMode === 'sets' && !selectedCardSet) {
        setSetsDisplayLimit(prev => {
          if (prev >= allSets.length) return prev;
          return Math.min(prev + 30, allSets.length);
        });
      } else if (activeTab === 'wishlist') {
        setWishDisplayLimit(prev => prev + 30);
      }
    }
  }, [activeTab, cardsViewMode, selectedCardSet, filteredCards.length, allSets.length]);

  // IntersectionObserver callback ref for Safari/iOS where onScroll doesn't fire reliably
  const loadMoreRef = useCallback((node) => {
    if (loadMoreObserver.current) loadMoreObserver.current.disconnect();
    if (!node) return;
    loadMoreObserver.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        console.log('[Collection] IntersectionObserver triggered - loading more');
        if (activeTab === 'cards' && cardsViewMode !== 'sets' && !selectedCardSet) {
          setCardsDisplayLimit(prev => {
            if (prev >= filteredCards.length) return prev;
            return Math.min(prev + 30, filteredCards.length);
          });
        } else if (activeTab === 'cards' && cardsViewMode === 'sets' && !selectedCardSet) {
          setSetsDisplayLimit(prev => {
            if (prev >= allSets.length) return prev;
            return Math.min(prev + 30, allSets.length);
          });
        } else if (activeTab === 'wishlist') {
          setWishDisplayLimit(prev => prev + 30);
        }
      }
    }, { rootMargin: '300px' });
    loadMoreObserver.current.observe(node);
  }, [activeTab, cardsViewMode, selectedCardSet, filteredCards.length, allSets.length]);

  // Get card count for a binder (computed from cards array)
  const getBinderCardCount = (binderId) => {
    return cards.filter(card => card.binder_id === binderId).length;
  };

  // Get total value for a binder
  const getBinderValue = (binderId) => {
    const binderCards = cards.filter(card => card.binder_id === binderId);
    const total = binderCards.reduce((sum, c) => sum + (cardPrices[c.id]?.price || 0), 0);
    return total > 0 ? formatPrice(total) : null;
  };

  // Handle refresh prices button
  const handleRefreshPrices = async () => {
    if (refreshCooldown || refreshing) {
      console.log('[Collection] Refresh blocked - cooldown:', refreshCooldown, 'refreshing:', refreshing);
      return;
    }
    console.log('[Collection] Starting price refresh for', cards.length, 'cards');
    setRefreshing(true);
    setRefreshProgress({ done: 0, total: 0 });

    const cardLookups = cards.map(card => ({
      id: card.id,
      name: card.english_name || card.name,
      set_name: card.set_name || '',
      set_number: card.set_number || card.card_id?.split('-').pop() || '',
      grade: card.grade || 'NM',
      variant: card.variant || '',
    }));

    const { updated } = await refreshCollectionPrices(cardLookups, (done, total) => {
      setRefreshProgress({ done, total });
    });

    // Re-read all prices
    const results = await getBulkCardPrices(cardLookups);
    const prices = {};
    let oldestRefresh = null;
    for (const [cardId, result] of results) {
      if (result.price != null && result.price > 0) {
        prices[cardId] = { price: result.price, formatted: result.formatted };
      }
      const ts = result.row?.last_refreshed_at;
      if (ts && (!oldestRefresh || new Date(ts) < new Date(oldestRefresh))) {
        oldestRefresh = ts;
      }
    }
    setCardPrices(prices);
    if (oldestRefresh) setLastCollectionRefresh(oldestRefresh);
    setRefreshing(false);
    showToast(`Prices updated for ${updated} cards`);
  };

  // Render star rating
  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(<StarIcon key={i} filled={i <= Math.round(rating)} />);
    }
    return stars;
  };

  // Handle card click - navigate to card detail page or toggle selection in bulk edit mode
  const handleCardClick = (card) => {
    if (bulkEditMode) {
      // Toggle selection
      setSelectedCards(prev =>
        prev.includes(card.id)
          ? prev.filter(id => id !== card.id)
          : [...prev, card.id]
      );
    } else {
      // Set navigation context for left/right arrows in CardDetail
      const cardIds = filteredCards.map(c => c.id);
      const currentIndex = cardIds.indexOf(card.id);
      setCardNavigationContext({
        type: 'collection',
        cardIds,
        currentIndex: currentIndex >= 0 ? currentIndex : 0,
        sourceLabel: isViewingOther ? `@${profile?.username}` : 'My Collection',
      });
      navigate(`/card/${card.id}`);
    }
  };

  // Toggle card selection for bulk edit
  const toggleCardSelection = (cardId) => {
    setSelectedCards(prev =>
      prev.includes(cardId)
        ? prev.filter(id => id !== cardId)
        : [...prev, cardId]
    );
  };

  // Handle bulk edit apply
  const handleBulkEditApply = async () => {
    if (selectedCards.length === 0 || !bulkEditMode) return;

    try {
      // Handle delete - show confirm modal
      if (bulkEditMode === 'delete') {
        setShowDeleteConfirm(true);
        return;
      }

      let updateData = {};

      switch (bulkEditMode) {
        case 'tradeable':
          updateData = { trading_status: 'TRADE' };
          break;
        case 'pc':
          updateData = { trading_status: 'PC' };
          break;
        case 'public':
          updateData = { is_private: false };
          break;
        case 'private':
          updateData = { is_private: true };
          break;
        default:
          return;
      }

      const { error } = await supabase
        .from('cards')
        .update(updateData)
        .in('id', selectedCards);

      if (error) throw error;

      // Update local state
      setCards(prev => prev.map(card =>
        selectedCards.includes(card.id)
          ? { ...card, ...updateData }
          : card
      ));

      // Reset bulk edit state
      setBulkEditMode(null);
      setSelectedCards([]);
    } catch (e) {
      console.error('Error applying bulk edit:', e);
    }
  };

  // Confirm and execute delete
  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await handleBulkDelete(selectedCards);
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  // Handle bulk card deletion (soft delete with cascade)
  const handleBulkDelete = async (cardIds) => {
    try {
      // 1. Check for cards in scheduled/confirmed trades
      const { data: scheduledInterests } = await supabase
        .from('trade_interests')
        .select(`
          card_id,
          trade_request_id,
          trade_requests!inner (
            id, status,
            trade_schedules (id, status)
          )
        `)
        .in('card_id', cardIds)
        .in('trade_requests.status', ['accepted']);

      const blockedCards = [];
      if (scheduledInterests) {
        for (const interest of scheduledInterests) {
          const schedules = interest.trade_requests?.trade_schedules || [];
          const hasScheduled = schedules.some(s => s.status === 'confirmed' || s.status === 'pending_confirmation');
          if (hasScheduled) blockedCards.push(interest.card_id);
        }
      }

      // Also check trade_requests.card_id directly for the initiating card
      const { data: scheduledRequests } = await supabase
        .from('trade_requests')
        .select('card_id, status, trade_schedules (id, status)')
        .in('card_id', cardIds)
        .in('status', ['accepted']);

      if (scheduledRequests) {
        for (const req of scheduledRequests) {
          const schedules = req.trade_schedules || [];
          const hasScheduled = schedules.some(s => s.status === 'confirmed' || s.status === 'pending_confirmation');
          if (hasScheduled && !blockedCards.includes(req.card_id)) blockedCards.push(req.card_id);
        }
      }

      if (blockedCards.length > 0) {
        const blockedNames = cards
          .filter(c => blockedCards.includes(c.id))
          .map(c => c.name)
          .join(', ');
        alert(`These cards are in scheduled trades and can't be deleted right now: ${blockedNames}. Cancel those trades first.`);
        return;
      }

      // 2. Soft delete the cards
      const { error: deleteError } = await supabase
        .from('cards')
        .update({ deleted_at: new Date().toISOString() })
        .in('id', cardIds);

      if (deleteError) throw deleteError;

      // 3. Posts cascade: remove from posts_cards, delete orphan posts
      for (const cardId of cardIds) {
        const { data: postLinks } = await supabase
          .from('posts_cards')
          .select('post_id')
          .eq('card_id', cardId);

        if (postLinks?.length) {
          // Remove this card from all posts
          await supabase.from('posts_cards').delete().eq('card_id', cardId);

          // Check each affected post for remaining cards
          for (const link of postLinks) {
            const { data: remaining } = await supabase
              .from('posts_cards')
              .select('id')
              .eq('post_id', link.post_id);

            if (!remaining || remaining.length === 0) {
              // No cards left, delete the post
              await supabase.from('posts').delete().eq('id', link.post_id);
            }
          }
        }
      }

      // 4. Active trades cascade: remove from trade_interests, notify
      const { data: activeInterests } = await supabase
        .from('trade_interests')
        .select('id, card_id, trade_request_id, user_id')
        .in('card_id', cardIds);

      if (activeInterests?.length) {
        const cardNameMap = {};
        cards.forEach(c => { cardNameMap[c.id] = c.name; });
        const userName = profile?.username || 'Someone';

        // Group by trade
        const byTrade = {};
        for (const interest of activeInterests) {
          if (!byTrade[interest.trade_request_id]) byTrade[interest.trade_request_id] = [];
          byTrade[interest.trade_request_id].push(interest);
        }

        for (const [reqId, interests] of Object.entries(byTrade)) {
          // Delete the interests
          await supabase
            .from('trade_interests')
            .delete()
            .in('id', interests.map(i => i.id));

          // Check remaining interests for this user in this trade
          const { data: remaining } = await supabase
            .from('trade_interests')
            .select('id')
            .eq('trade_request_id', reqId)
            .eq('user_id', activeProfile.id);

          // Get trade request to find partner
          const { data: tradeReq } = await supabase
            .from('trade_requests')
            .select('requester_id, card_owner_id, status')
            .eq('id', reqId)
            .single();

          if (!tradeReq || tradeReq.status === 'completed' || tradeReq.status === 'cancelled') continue;

          const partnerId = tradeReq.requester_id === activeProfile.id
            ? tradeReq.card_owner_id
            : tradeReq.requester_id;

          const removedNames = interests.map(i => cardNameMap[i.card_id] || 'a card').join(', ');

          if (!remaining || remaining.length === 0) {
            // No cards left for this user in the trade, cancel it
            await supabase.from('trade_requests').update({ status: 'cancelled' }).eq('id', reqId);
            await supabase.from('messages').insert({
              trade_request_id: reqId,
              sender_id: '550e8400-e29b-41d4-a716-446655440000',
              message: `@${userName} removed all their cards from this trade. The trade has been cancelled.`,
              message_type: 'system',
              is_ignored: false,
            });
            if (partnerId) sendCardRemovedFromTradeNotification(partnerId, userName, removedNames, reqId);
          } else {
            // Other cards remain, just notify
            await supabase.from('messages').insert({
              trade_request_id: reqId,
              sender_id: '550e8400-e29b-41d4-a716-446655440000',
              message: `@${userName} removed ${removedNames} from their collection. The trade can continue with the remaining cards.`,
              message_type: 'system',
              is_ignored: false,
            });
            if (partnerId) sendCardRemovedFromTradeNotification(partnerId, userName, removedNames, reqId);
          }
        }
      }

      // 5. Update local state
      setCards(prev => prev.filter(c => !cardIds.includes(c.id)));
      setBulkEditMode(null);
      setSelectedCards([]);

    } catch (e) {
      console.error('Error deleting cards:', e);
      alert('Error deleting cards. Please try again.');
    }
  };

  // Cancel bulk edit mode
  const cancelBulkEdit = () => {
    setBulkEditMode(null);
    setSelectedCards([]);
  };

  // Handle add card from Cards tab
  const handleAddCard = ({ type, binder }) => {
    setAddCardModalOpen(false);
    navigate('/bulk-scanner');
  };

  // Handle create binder
  const handleCreateBinder = async ({ name, image, isPrivate }) => {
    try {
      // Generate UUID
      const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });

      const { error } = await supabase
        .from('binders')
        .insert({
          id: uuid,
          user_id: profile.id,
          name,
          image,
          card_count: 0,
          last_updated: new Date().toISOString(),
          created_at: new Date().toISOString(),
        });

      if (error) throw error;

      // Refresh binders
      const { data: bindersData } = await supabase
        .from('binders')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false });

      if (bindersData) setBinders(bindersData);
      // Trigger congrats if first_binder tutorial not yet completed
      handleBinderAdded();
    } catch (err) {
      console.error('Error creating binder:', err);
    }
  };

  // Handle add new card to binder (from inside binder)
  const handleAddNewCardToBinder = (binder) => {
    setAddToBinderModalOpen(false);
    navigate('/bulk-scanner');
  };

  // Handle move cards to binder
  const handleMoveCardsToBinder = async (cardsToMove, targetBinder) => {
    try {
      const cardIds = cardsToMove.map((c) => c.id);

      const { error } = await supabase
        .from('cards')
        .update({ binder_id: targetBinder.id })
        .in('id', cardIds);

      if (error) throw error;

      // Refresh cards
      const { data: cardsData } = await supabase
        .from('cards')
        .select('*')
        .eq('user_id', profile.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (cardsData) setCards(cardsData);
    } catch (err) {
      console.error('Error moving cards:', err);
    }
  };

  // Stats Panel Component
  const StatsPanel = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={styles.statsPanelOverlay}
      onClick={() => setShowStatsPanel(false)}
    >
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.95 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        style={styles.statsPanel}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.statsPanelHeader}>
          <h2 style={styles.statsPanelTitle}>{profile?.username}'s Stats</h2>
          <button style={styles.statsPanelClose} onClick={() => setShowStatsPanel(false)}>
            <CloseIcon />
          </button>
        </div>

        {/* User Actions - only show when viewing other user */}
        {isViewingOther && (
          <div style={styles.userActionsSection}>
            <button
              style={{
                ...styles.userActionButton,
                ...(isFollowing ? styles.userActionButtonActive : {}),
              }}
              onClick={handleToggleFollow}
            >
              <span style={styles.userActionText}>
                {isFollowing ? 'Following' : 'Follow'}
              </span>
            </button>
            <button style={styles.userActionButton} onClick={handleBlockUser}>
              <span style={styles.userActionText}>Block</span>
            </button>
            <button style={styles.userActionButtonDanger} onClick={handleReportUser}>
              <span style={styles.userActionTextDanger}>Report</span>
            </button>
          </div>
        )}

        {/* Trade Reputation */}
        <div style={styles.tradeReputationSection}>
          <h3 style={styles.sectionTitle}>Trade Reputation</h3>
          {tradeStats.totalReviews > 0 ? (
            <>
              <div style={styles.overallRating}>
                <div style={styles.starsRow}>
                  {renderStars(tradeStats.averageRating)}
                </div>
                <span style={styles.ratingText}>
                  {tradeStats.averageRating.toFixed(1)} ({tradeStats.totalReviews} reviews)
                </span>
              </div>

              {/* Rating Breakdown */}
              <div style={styles.ratingBreakdown}>
                {Object.entries(tradeStats.ratingBreakdown).map(([key, value]) => (
                  <div key={key} style={styles.ratingRow}>
                    <span style={styles.ratingLabel}>
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </span>
                    <div style={styles.ratingBarContainer}>
                      <div
                        style={{
                          ...styles.ratingBar,
                          width: `${(value / 5) * 100}%`,
                        }}
                      />
                    </div>
                    <span style={styles.ratingValue}>{value.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={styles.noReviews}>
              <span style={styles.noReviewsText}>No trade reviews yet</span>
              <span style={styles.noReviewsSubtext}>Complete trades to build your reputation</span>
            </div>
          )}
        </div>

        {/* All Badges */}
        <div style={{ padding: '0 20px 12px' }}>
          <button
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px',
              backgroundColor: theme.bgCard,
              borderRadius: '12px',
              border: `1px solid ${theme.border}`,
              cursor: 'pointer',
              outline: 'none',
            }}
            onClick={() => {
              setShowStatsPanel(false);
              setAllBadgesOpen(true);
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                {['charter', 'trader', 'officer', 'researcher', 'collector', 'organized'].map((type) => (
                  <BadgeProgressRing key={type} type={type} data={badgeData} size={24} showLabel={false} theme={theme} />
                ))}
              </div>
              <span style={{ color: theme.text, fontSize: '14px', fontWeight: '600' }}>All Badges</span>
            </div>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        {/* Quick Stats */}
        <div style={styles.quickStatsGrid}>
          <div style={styles.quickStatCard}>
            <div style={styles.quickStatIcon}>
              <BindersIcon />
            </div>
            <span style={styles.quickStatValue}>{binders.length}</span>
            <span style={styles.quickStatLabel}>Binders</span>
          </div>
          <div style={styles.quickStatCard}>
            <div style={styles.quickStatIcon}>
              <CardsIcon />
            </div>
            <span style={styles.quickStatValue}>{cards.length}</span>
            <span style={styles.quickStatLabel}>Cards</span>
          </div>
          <div style={styles.quickStatCard}>
            <div style={styles.quickStatIcon}>
              <TrendingUpIcon />
            </div>
            <span style={styles.quickStatValue}>{tradeStats.totalTrades}</span>
            <span style={styles.quickStatLabel}>Trades</span>
          </div>
        </div>

        {/* Collection Value */}
        {pricingEnabled && totalCollectionValue && (
          <div style={{ padding: '0 20px 12px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px',
              backgroundColor: theme.bgCard,
              borderRadius: '12px',
              border: `1px solid ${theme.border}`,
            }}>
              <span style={{ color: theme.textSecondary, fontSize: '13px', fontWeight: '500' }}>Est. Collection Value</span>
              <span style={{ color: '#4ade80', fontSize: '20px', fontWeight: '700' }}>{totalCollectionValue}</span>
            </div>
          </div>
        )}

        {/* Trade Activity */}
        <div style={styles.tradeReputationSection}>
          <h3 style={styles.sectionTitle}>Trade Activity</h3>
          <div style={styles.tradeActivityGrid}>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: theme.accent }}>{tradeStats.messagesStarted}</span>
              <span style={styles.tradeActivityLabel}>Started</span>
            </div>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: theme.warning }}>{tradeStats.scheduled}</span>
              <span style={styles.tradeActivityLabel}>Scheduled</span>
            </div>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: theme.success }}>{tradeStats.completed}</span>
              <span style={styles.tradeActivityLabel}>Completed</span>
            </div>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: theme.textSecondary }}>{tradeStats.incomplete}</span>
              <span style={styles.tradeActivityLabel}>Incomplete</span>
            </div>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: tradeStats.cancelled > 0 ? theme.error : theme.textSecondary }}>{tradeStats.cancelled || 0}</span>
              <span style={styles.tradeActivityLabel}>Cancelled{tradeStats.cancelledLastMinute > 0 ? ` (${tradeStats.cancelledLastMinute} late)` : ''}</span>
            </div>
            <div style={styles.tradeActivityItem}>
              <span style={{ ...styles.tradeActivityValue, color: theme.success }}>{tradeStats.completionRate}%</span>
              <span style={styles.tradeActivityLabel}>Success</span>
            </div>
          </div>

          {/* Activity Indicators */}
          <div style={styles.activityIndicatorsRow}>
            <div style={styles.activityIndicator}>
              <span style={styles.activityLabel}>Last Active</span>
              <span style={{ ...styles.activityValue, color: tradeStats.lastActive ? theme.success : theme.textMuted }}>
                {tradeStats.lastActive
                  ? (() => {
                      const diff = Date.now() - new Date(tradeStats.lastActive);
                      const hours = Math.floor(diff / (1000 * 60 * 60));
                      const days = Math.floor(hours / 24);
                      if (days > 30) return '30d+';
                      if (days > 0) return `${days}d ago`;
                      if (hours > 0) return `${hours}h ago`;
                      return 'Just now';
                    })()
                  : 'None'}
              </span>
            </div>
            <div style={styles.activityIndicator}>
              <span style={styles.activityLabel}>Avg Response</span>
              <span style={{
                ...styles.activityValue,
                color: tradeStats.avgResponseTime !== null
                  ? (tradeStats.avgResponseTime < 2 ? theme.success : tradeStats.avgResponseTime < 12 ? theme.warning : theme.error)
                  : theme.textMuted
              }}>
                {tradeStats.avgResponseTime !== null
                  ? (() => {
                      const hours = tradeStats.avgResponseTime;
                      if (hours < 1) return `${Math.round(hours * 60)}m`;
                      if (hours < 24) return `${Math.round(hours)}h`;
                      return `${Math.round(hours / 24)}d`;
                    })()
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  // Chevron Icon for collapsible sections
  const ChevronIcon = ({ isOpen }) => (
    <motion.svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      animate={{ rotate: isOpen ? 180 : 0 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
    >
      <polyline points="6 9 12 15 18 9" />
    </motion.svg>
  );

  // Close smart search handler
  const closeSmartSearch = useCallback(() => {
    setSmartSearch({ open: false, query: '', expandedSection: null });
    setSearchQuery('');
  }, []);

  // Smart search card click handler
  const handleSmartSearchCardClick = useCallback((card, type) => {
    closeSmartSearch();
    if (type === 'owned') {
      navigate(`/card/${card.id}`);
    } else if (type === 'wish') {
      navigate(`/card/tcg_${card.tcgdex_card_id.replace('-', '_')}`);
    } else if (type === 'tcg') {
      const parts = card.id.split('-');
      const setId = parts.slice(0, -1).join('-');
      const localId = parts[parts.length - 1];
      navigate(`/card/tcg_${setId}_${localId}`);
    }
  }, [closeSmartSearch, navigate]);

  // Smart search set click handler
  const handleSmartSearchSetClick = useCallback((set) => {
    closeSmartSearch();
    setSelectedCardSet(set.id);
    setSetCardFilter('all');
  }, [closeSmartSearch]);

  // Memoized search panel data
  const searchPanelData = useMemo(() => {
    if (!smartSearch.open || !smartSearch.query || smartSearch.query.length < 2) {
      return null;
    }

    const matchingOwnedCards = cards.filter(card =>
      card.name?.toLowerCase().includes(smartSearch.query.toLowerCase())
    );

    const matchingWishCards = wishListItems.filter(item =>
      item.card_name?.toLowerCase().includes(smartSearch.query.toLowerCase())
    );

    const matchingSets = allSets.filter(set =>
      set.name?.toLowerCase().includes(smartSearch.query.toLowerCase())
    ).slice(0, 20);

    const isCardOwned = (tcgId) => cards.some(c => c.card_id === tcgId);
    const isCardWished = (tcgId) => wishListItems.some(w => w.tcgdex_card_id === tcgId);

    return {
      matchingOwnedCards,
      matchingWishCards,
      matchingSets,
      isCardOwned,
      isCardWished,
      sections: [
        {
          id: 'yourCards',
          title: 'Your Cards',
          count: matchingOwnedCards.length,
          color: theme.accent,
          bgColor: theme.accentBg,
        },
        {
          id: 'wishList',
          title: 'Wish List',
          count: matchingWishCards.length,
          color: theme.warning,
          bgColor: theme.warningBg,
        },
        {
          id: 'allCards',
          title: `All "${smartSearch.query}"`,
          count: tcgSearchResults.length,
          color: theme.purple,
          bgColor: theme.purpleBg,
          loading: tcgSearchLoading,
        },
        {
          id: 'sets',
          title: 'Sets',
          count: matchingSets.length,
          color: theme.success,
          bgColor: theme.successBg,
        },
      ],
    };
  }, [smartSearch.open, smartSearch.query, cards, wishListItems, allSets, tcgSearchResults, tcgSearchLoading, theme]);

  // Smart Search Results Panel - memoized JSX
  const searchResultsPanel = useMemo(() => {
    if (!searchPanelData) return null;

    const { matchingOwnedCards, matchingWishCards, matchingSets, isCardOwned, isCardWished, sections } = searchPanelData;

    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.2 }}
        style={styles.searchResultsPanel}
      >
        {/* Backdrop blur overlay */}
        <div
          style={styles.searchResultsBackdrop}
          onClick={closeSmartSearch}
        />

        {/* Results Container */}
        <motion.div
          style={styles.searchResultsContainer}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25, delay: 0.05 }}
        >
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              style={styles.searchSection}
            >
              {/* Section Header */}
              <motion.button
                style={{
                  ...styles.searchSectionHeader,
                  borderColor: smartSearch.expandedSection === section.id ? section.color : theme.border,
                }}
                onClick={() => section.count > 0 && toggleSearchSection(section.id)}
                whileTap={{ scale: 0.98 }}
                disabled={section.count === 0 && !section.loading}
              >
                <div style={styles.searchSectionLeft}>
                  <span style={{
                    ...styles.searchSectionTitle,
                    color: section.count > 0 ? theme.text : theme.textSecondary,
                  }}>
                    {section.title}
                  </span>
                </div>
                <div style={styles.searchSectionRight}>
                  {section.loading ? (
                    <motion.div
                      style={styles.searchSectionSpinner}
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                  ) : (
                    <motion.span
                      style={{
                        ...styles.searchSectionCount,
                        backgroundColor: section.count > 0 ? section.bgColor : theme.bgCard,
                        color: section.count > 0 ? section.color : theme.textSecondary,
                      }}
                      initial={{ scale: 0.8 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      {section.count}
                    </motion.span>
                  )}
                  {section.count > 0 && (
                    <ChevronIcon isOpen={smartSearch.expandedSection === section.id} />
                  )}
                </div>
              </motion.button>

              {/* Expanded Content */}
              <AnimatePresence>
                {smartSearch.expandedSection === section.id && section.count > 0 && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    style={styles.searchSectionContent}
                  >
                    <div style={styles.searchSectionScrollable}>
                    <div style={styles.searchCardsGrid}>
                      {section.id === 'yourCards' && matchingOwnedCards.map((card) => (
                        <motion.div
                          key={card.id}
                          style={styles.searchCardItem}
                          onClick={() => handleSmartSearchCardClick(card, 'owned')}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <CardImage
                            digitalImage={card.digital_image_url}
                            userPhoto={card.original_image_url}
                            name={card.name}
                            number={card.number}
                            setName={card.set_name}
                            imgStyle={styles.searchCardImg}
                            theme={theme}
                          />
                          <div style={{
                            ...styles.searchCardBadge,
                            backgroundColor: theme.accent,
                          }}>O</div>
                        </motion.div>
                      ))}
                      {section.id === 'wishList' && matchingWishCards.map((item) => {
                        // Extract localId same way as normal wish list view
                        const cardNum = item.tcgdex_card_id?.replace(`${item.set_id}-`, '') || '';
                        return (
                        <motion.div
                          key={item.id}
                          style={styles.searchCardItem}
                          onClick={() => handleSmartSearchCardClick(item, 'wish')}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <CardImage
                            digitalImage={item.image_url ? `${item.image_url}/low.webp` : `https://assets.tcgdex.net/en/${item.set_id}/${cardNum}/low.webp`}
                            name={item.card_name}
                            number={cardNum}
                            setName={item.set_name}
                            imgStyle={styles.searchCardImg}
                            theme={theme}
                          />
                          <div style={{
                            ...styles.searchCardBadge,
                            backgroundColor: theme.warning,
                          }}>W</div>
                        </motion.div>
                        );
                      })}
                      {section.id === 'allCards' && tcgSearchResults.map((card) => {
                        const owned = isCardOwned(card.id);
                        const wished = isCardWished(card.id);
                        return (
                          <motion.div
                            key={card.id}
                            style={{
                              ...styles.searchCardItem,
                              opacity: (owned || wished) ? 1 : 0.6,
                            }}
                            onClick={() => handleSmartSearchCardClick(card, 'tcg')}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <CardImage
                              digitalImage={card.image ? `${card.image}/low.webp` : null}
                              name={card.name}
                              number={card.localId}
                              setName={card.set?.name}
                              imgStyle={styles.searchCardImg}
                              theme={theme}
                            />
                            {(owned || wished) && (
                              <div style={styles.searchCardStatus}>
                                {owned && <span style={{ ...styles.searchCardBadgeSmall, backgroundColor: theme.accent }}>O</span>}
                                {wished && <span style={{ ...styles.searchCardBadgeSmall, backgroundColor: theme.warning }}>W</span>}
                              </div>
                            )}
                          </motion.div>
                        );
                      })}
                      {section.id === 'sets' && matchingSets.map((set) => (
                        <motion.div
                          key={set.id}
                          style={styles.searchSetItem}
                          onClick={() => handleSmartSearchSetClick(set)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {set.logo && (
                            <img
                              src={`${set.logo}.png`}
                              alt={set.name}
                              style={styles.searchSetLogo}
                            />
                          )}
                          <span style={styles.searchSetName}>{set.name}</span>
                        </motion.div>
                      ))}
                    </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    );
  }, [searchPanelData, smartSearch.open, smartSearch.expandedSection, tcgSearchLoading, theme, toggleSearchSection, handleSmartSearchCardClick, handleSmartSearchSetClick, closeSmartSearch, tcgSearchResults]);

  // Cards View Component
  const cardsView = useMemo(() => (
    <div style={styles.viewContainer}>
      {/* Cards Grid */}
      <LayoutGroup>
        <motion.div
          layout
          style={{
            ...styles.cardsGrid,
            gridTemplateColumns: cardZoom === 2
              ? 'repeat(auto-fill, minmax(140px, 1fr))'
              : cardZoom === 1
              ? 'repeat(auto-fill, minmax(105px, 1fr))'
              : 'repeat(auto-fill, minmax(75px, 1fr))',
            gap: cardZoom === 2 ? '12px' : cardZoom === 1 ? '10px' : '8px',
          }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* Add Card Placeholder */}
          {!isViewingOther && (
            <motion.div
              layout
              style={{
                ...styles.addCardPlaceholder,
                ...(cardZoom === 0 ? styles.addCardPlaceholderSmall : {}),
              }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              onClick={() => navigate('/bulk-scanner')}
            >
              <div style={styles.addCardInner}>
                <PlusIcon />
                <AnimatePresence>
                  {cardZoom >= 1 && (
                    <motion.span
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      style={styles.addCardText}
                    >
                      Add Card
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {/* Card Items */}
          {filteredCards.slice(0, cardsDisplayLimit).map((card) => {
            const isSelected = selectedCards.includes(card.id);
            return (
            <motion.div
              key={card.id}
              layout
              style={{
                ...styles.cardItem,
                ...(bulkEditMode && isSelected ? styles.cardItemSelected : {}),
              }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              onClick={() => handleCardClick(card)}
            >
              <div style={styles.cardImageWrapper}>
                <CardImage
                  digitalImage={card.digital_image_url}
                  userPhoto={card.original_image_url}
                  name={card.name}
                  number={card.number}
                  setName={card.set_name}
                  preferPhoto={imageFilter === 'photo'}
                  imgStyle={styles.cardImage}
                  theme={theme}
                  loading="lazy"
                />
                {/* Trade status badge - bottom left (show for all users) */}
                <div style={{
                  ...styles.cardBadge,
                  ...styles.cardBadgeBottomLeft,
                  ...(cardZoom === 0 ? styles.cardBadgeSmall : {}),
                  backgroundColor: card.trading_status === 'TRADE' ? 'rgba(34, 197, 94, 0.9)' : 'rgba(14, 165, 233, 0.9)',
                }}>
                  <span style={styles.cardBadgeText}>
                    {card.trading_status === 'TRADE' ? 'T' : 'PC'}
                  </span>
                </div>
                {/* Price badge - bottom right */}
                {pricingEnabled && cardPrices[card.id] && (
                  <div style={{
                    ...styles.cardBadge,
                    ...styles.cardBadgeBottomRight,
                    ...(cardZoom === 0 ? styles.cardBadgeSmall : {}),
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    color: '#4ade80',
                  }}>
                    <span style={styles.cardBadgeText}>
                      {cardPrices[card.id].formatted}
                    </span>
                  </div>
                )}
                {/* Visibility badge - only on own collection, only if private */}
                {!isViewingOther && card.is_private && (
                  <div style={{
                    ...styles.cardBadge,
                    ...(bulkEditMode ? styles.cardBadgeTopLeft : styles.cardBadgeTopRight),
                    ...(cardZoom === 0 ? styles.cardBadgeSmall : {}),
                    backgroundColor: 'rgba(107, 114, 128, 0.9)',
                  }}>
                    <svg width={cardZoom === 0 ? 8 : 10} height={cardZoom === 0 ? 8 : 10} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 17a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2 2 2 0 0 0-2 2v6a2 2 0 0 0 2 2zm6-9a6 6 0 0 0-12 0v2H4v10h16V10h-2V8zm-2 0v2H8V8a4 4 0 1 1 8 0z"/>
                    </svg>
                  </div>
                )}
                {/* Bulk edit selection indicator */}
                {bulkEditMode && (
                  <div style={{
                    ...styles.selectionIndicator,
                    ...(isSelected ? styles.selectionIndicatorActive : {}),
                  }}>
                    {isSelected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                )}
              </div>
              <AnimatePresence>
                {cardZoom >= 1 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    style={styles.cardInfo}
                    transition={{ duration: 0.25 }}
                  >
                    <span style={styles.cardName}>{card.name}</span>
                    {cardZoom === 2 && <span style={styles.cardGrade}>{card.grade}</span>}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
            );
          })}
        </motion.div>
      </LayoutGroup>
      <div ref={loadMoreRef} style={{ height: 1 }} />
    </div>
  ), [cardZoom, filteredCards, cardsDisplayLimit, selectedCards, bulkEditMode, isViewingOther, styles, handleCardClick, cardPrices, pricingEnabled]);

  // Binders View Component
  const bindersView = useMemo(() => (
    <div style={styles.viewContainer}>
      <div style={styles.bindersGrid}>
        {/* Add Binder Placeholder */}
        {!isViewingOther && (
          <div style={styles.binderItem} onClick={() => setAddBinderModalOpen(true)}>
            <div style={styles.addBinderPlaceholder}>
              <PlusIcon />
            </div>
            <span style={styles.binderName}>Add Binder</span>
            <span style={styles.binderCount}>Create new</span>
          </div>
        )}

        {/* Binder Items */}
        {sortedBinders.map((binder) => (
          <div
            key={binder.id}
            style={styles.binderItem}
            onClick={() => handleBinderClick(binder)}
          >
            <div style={styles.binderImageWrapper}>
              <img
                src={binderImages[binder.image] || binderImages.blue}
                alt={binder.name}
                style={styles.binderImage}
              />
              <div style={styles.binderGlow} />
            </div>
            <span style={styles.binderName}>{binder.name}</span>
            <span style={styles.binderCount}>{getBinderCardCount(binder.id)} cards</span>
            {pricingEnabled && getBinderValue(binder.id) && (
              <span style={styles.binderValue}>{getBinderValue(binder.id)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  ), [sortedBinders, styles, handleBinderClick, getBinderCardCount, pricingEnabled, getBinderValue]);

  // Wish List View Component - memoized (Two-tier: Top 20 + More)
  const wishListView = useMemo(() => {
    // Loading state
    if (wishListLoading) {
      return (
        <div style={styles.viewContainer}>
          <div style={styles.wishListEmpty}>
            <div style={styles.spinner} />
            <span style={{ color: theme.textSecondary, marginTop: '12px' }}>Loading wish list...</span>
          </div>
        </div>
      );
    }

    // Apply search filter first
    const searchFilteredItems = searchQuery && searchQuery.length >= 2
      ? wishListItems.filter(item => item.card_name?.toLowerCase().includes(searchQuery.toLowerCase()))
      : wishListItems;

    // Separate priority (Top 20) and non-priority (More) cards
    const priorityCards = searchFilteredItems.filter(item => item.is_priority === true);
    const moreCards = searchFilteredItems.filter(item => item.is_priority !== true);

    // Sort cards based on current sort
    const sortCards = (cards) => {
      return [...cards].sort((a, b) => {
        switch (wishListSortBy) {
          case 'name-asc':
            return (a.card_name || '').localeCompare(b.card_name || '');
          case 'name-desc':
            return (b.card_name || '').localeCompare(a.card_name || '');
          case 'oldest':
            return new Date(a.added_at) - new Date(b.added_at);
          default: // 'newest' or 'recent'
            return new Date(b.added_at) - new Date(a.added_at);
        }
      });
    };

    const sortedPriorityCards = sortCards(priorityCards);
    const sortedMoreCards = sortCards(moreCards);

    // Match Cards tab grid sizing
    const gridTemplate = wishListZoom === 2
      ? 'repeat(auto-fill, minmax(140px, 1fr))'
      : wishListZoom === 1
      ? 'repeat(auto-fill, minmax(105px, 1fr))'
      : 'repeat(auto-fill, minmax(75px, 1fr))';
    const gridGap = wishListZoom === 2 ? '12px' : wishListZoom === 1 ? '10px' : '8px';

    // Long press handler
    const handleLongPress = (card, e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isViewingOther) {
        setSelectedWishCard(card);
        setWishCardMenuOpen(true);
      }
    };

    // Card click handler
    const handleCardClick = (card) => {
      const localId = card.tcgdex_card_id?.replace(`${card.set_id}-`, '') || '';
      const allCards = [...sortedPriorityCards, ...sortedMoreCards];
      const cardIds = allCards.map(c => {
        const lid = c.tcgdex_card_id?.replace(`${c.set_id}-`, '') || '';
        return `tcg_${c.set_id}_${lid}`;
      });
      const currentIndex = allCards.findIndex(c => c.id === card.id);
      setCardNavigationContext({
        type: 'wishlist',
        cardIds,
        currentIndex: currentIndex >= 0 ? currentIndex : 0,
        sourceLabel: isViewingOther ? `${profile?.username || 'User'}'s Wish List` : 'My Wish List',
      });
      navigate(`/card/tcg_${card.set_id}_${localId}`);
    };

    // Render a wish card - matches Cards tab styling
    const renderWishCard = (card, isPriority) => {
      const cardNum = card.tcgdex_card_id?.replace(`${card.set_id}-`, '') || '';
      let pressTimer = null;

      return (
        <div
          key={card.id}
          style={styles.cardItem}
          onClick={() => handleCardClick(card)}
          onTouchStart={(e) => {
            pressTimer = setTimeout(() => handleLongPress(card, e), 500);
          }}
          onTouchEnd={() => clearTimeout(pressTimer)}
          onTouchMove={() => clearTimeout(pressTimer)}
          onContextMenu={(e) => handleLongPress(card, e)}
        >
          <div style={styles.cardImageWrapper}>
            <CardImage
              digitalImage={card.image_url ? `${card.image_url}/low.webp` : `https://assets.tcgdex.net/en/${card.set_id}/${cardNum}/low.webp`}
              name={card.card_name}
              number={cardNum}
              setName={card.set_name}
              imgStyle={styles.cardImage}
              theme={theme}
              loading="lazy"
            />
          </div>
          {wishListZoom >= 1 && (
            <div style={styles.cardInfo}>
              <span style={styles.cardName}>{card.card_name}</span>
            </div>
          )}
        </div>
      );
    };

    // Empty state
    if (wishListItems.length === 0) {
      return (
        <div style={styles.viewContainer}>
          <div style={styles.wishListEmpty}>
            <WishListIcon />
            <h3 style={{ ...styles.wishListEmptyTitle, color: theme.text }}>No Wish List Items</h3>
            <p style={{ ...styles.wishListEmptyText, color: theme.textSecondary }}>
              {isViewingOther
                ? "This user hasn't added any cards to their wish list yet."
                : "Search for cards you want and add them to your wish list."}
            </p>
            {!isViewingOther && (
              <button
                style={{ ...styles.wishListAddButton, backgroundColor: theme.warning, color: '#000' }}
                onClick={() => setWishListSearchOpen(true)}
              >
                <WishListIcon />
                <span>Add to Wish List</span>
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={styles.viewContainer}>
        {/* Top 20 Section */}
        <div style={{
          ...styles.wishPrioritySection,
          backgroundColor: theme.warningBg,
          borderColor: theme.warning,
        }}>
          <div style={styles.wishSectionHeader}>
            <div style={styles.wishSectionTitleRow}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill={theme.warning} stroke={theme.warning} strokeWidth="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              <span style={{ ...styles.wishSectionTitle, color: theme.warning }}>Top 20</span>
            </div>
            <span style={{ ...styles.wishSectionCount, color: theme.warning }}>
              {priorityCards.length}/20
            </span>
          </div>

          {sortedPriorityCards.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: gridTemplate,
              gap: gridGap,
              alignItems: 'start',
            }}>
              {sortedPriorityCards.map(card => renderWishCard(card, true))}
            </div>
          ) : (
            <div style={styles.wishSectionEmpty}>
              <span style={{ color: theme.textSecondary, fontSize: '13px' }}>
                {isViewingOther ? 'No priority cards' : 'Add cards to build your Top 20'}
              </span>
            </div>
          )}
        </div>

        {/* More Section */}
        {(sortedMoreCards.length > 0 || !isViewingOther) && (
          <div style={{
            ...styles.wishMoreSection,
            backgroundColor: theme.bgCard,
            borderColor: theme.border,
          }}>
            <div style={styles.wishSectionHeader}>
              <div style={styles.wishSectionTitleRow}>
                <span style={{ ...styles.wishSectionTitle, color: theme.text }}>More Wishes</span>
              </div>
              <span style={{ ...styles.wishSectionCount, color: theme.textSecondary }}>
                {moreCards.length}
              </span>
            </div>

            {sortedMoreCards.length > 0 ? (
              <>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: gridTemplate,
                  gap: gridGap,
                  alignItems: 'start',
                }}>
                  {sortedMoreCards.slice(0, wishDisplayLimit).map(card => renderWishCard(card, false))}
                </div>
              </>
            ) : (
              <div style={styles.wishSectionEmpty}>
                <span style={{ color: theme.textSecondary, fontSize: '13px' }}>
                  Cards beyond your Top 20 will appear here
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }, [wishListLoading, wishListItems, wishListSortBy, wishListZoom, wishDisplayLimit, isViewingOther, profile, styles, theme, navigate, setCardNavigationContext, setWishListSearchOpen]);

  // Refresh wish list data
  const refreshWishList = async () => {
    if (profile?.id) {
      const { data: wishData, count } = await supabase
        .from('wish_list')
        .select('*', { count: 'exact' })
        .eq('user_id', profile.id)
        .order('added_at', { ascending: false });
      setWishListItems(wishData || []);
      setWishListCount(count || 0);
    }
  };

  // Group wish list items by set
  const wishListBySet = wishListItems.reduce((acc, item) => {
    const setId = item.set_id || 'unknown';
    if (!acc[setId]) {
      acc[setId] = {
        set_id: setId,
        set_name: item.set_name || 'Unknown Set',
        cards: [],
      };
    }
    acc[setId].cards.push(item);
    return acc;
  }, {});

  // Convert to array, filter by search, and sort
  const wishListSets = Object.values(wishListBySet)
    .filter(set => true) // Search filtering handled by smart search panel
    .sort((a, b) => {
      switch (wishListSortBy) {
        case 'name-asc':
          return a.set_name.localeCompare(b.set_name);
        case 'name-desc':
          return b.set_name.localeCompare(a.set_name);
        case 'most-cards':
          return b.cards.length - a.cards.length;
        case 'least-cards':
          return a.cards.length - b.cards.length;
        default:
          return b.cards.length - a.cards.length;
      }
    });

  // Cards Sets View - shows all sets with owned card counts
  const cardsSetsView = useMemo(() => {
    // Loading state
    if (allSetsLoading) {
      return (
        <div style={styles.viewContainer}>
          <div style={styles.wishListEmpty}>
            <div style={styles.spinner} />
            <span style={{ color: theme.textSecondary, marginTop: '12px' }}>Loading sets...</span>
          </div>
        </div>
      );
    }

    // Calculate owned count per set
    const getOwnedCount = (setId) => {
      return cards.filter(c => c.card_id?.startsWith(`${setId}-`)).length;
    };

    // Calculate wish count per set
    const getWishCount = (setId) => {
      return wishListItems.filter(w => w.tcgdex_card_id?.startsWith(`${setId}-`)).length;
    };

    // Filter sets by search query
    let filteredSets = allSets
      .filter(set => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (set.name || '').toLowerCase().includes(query);
      });

    // Apply owned filter
    if (cardsSetsFilter === 'owned') {
      filteredSets = filteredSets.filter(set => getOwnedCount(set.id) > 0);
    }

    // Apply sorting using sortIndex (from API's releaseDate sort)
    filteredSets = filteredSets.sort((a, b) => {
      switch (cardsSetsSort) {
        case 'a-z':
          return (a.name || '').localeCompare(b.name || '');
        case 'z-a':
          return (b.name || '').localeCompare(a.name || '');
        case 'oldest':
          // Higher sortIndex = older set
          return (b.sortIndex ?? 999) - (a.sortIndex ?? 999);
        case 'newest':
        default:
          // Lower sortIndex = newer set
          return (a.sortIndex ?? 999) - (b.sortIndex ?? 999);
      }
    }).slice(0, searchQuery ? 100 : setsDisplayLimit);

    // Zoom controls: 0 = 3 cols, 1 = 2 cols, 2 = 1 col
    const gridColumns = cardsSetsZoom === 0 ? 3 : cardsSetsZoom === 1 ? 2 : 1;
    const logoHeight = cardsSetsZoom === 0 ? '50px' : cardsSetsZoom === 1 ? '70px' : '100px';
    const imgHeight = cardsSetsZoom === 0 ? '36px' : cardsSetsZoom === 1 ? '54px' : '80px';

    return (
      <div style={styles.viewContainer}>
        <div style={{
          ...styles.setsBrowseGrid,
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
        }}>
          {filteredSets.map((set) => {
            const ownedCount = getOwnedCount(set.id);
            const wishCount = getWishCount(set.id);
            const totalCount = set.cardCount?.total || set.cardCount?.official || '?';
            const isEmpty = ownedCount === 0 && wishCount === 0;

            return (
              <div
                key={set.id}
                style={{
                  ...styles.setBrowseCard,
                  opacity: isEmpty ? 0.4 : 1,
                  filter: isEmpty ? 'grayscale(50%)' : 'none',
                }}
                onClick={() => {
                  setSelectedCardSet(set.id);
                  setSetCardFilter('all'); // Reset filter when entering set
                }}
              >
                <div style={{ ...styles.setBrowseLogo, height: logoHeight }}>
                  {set.logo ? (
                    <img
                      src={`${set.logo}.png`}
                      alt={set.name}
                      style={{ ...styles.setBrowseLogoImg, height: imgHeight }}
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div style={{
                    ...styles.setBrowseLogoFallback,
                    display: set.logo ? 'none' : 'flex'
                  }}>
                    {set.name?.[0] || '?'}
                  </div>
                </div>
                <div style={styles.setBrowseInfo}>
                  {cardsSetsZoom >= 1 && <span style={styles.setBrowseName}>{set.name}</span>}
                  <div style={styles.setBrowseCounts}>
                    <span style={styles.setBrowseCountOwned}>{ownedCount}/{totalCount}</span>
                    <span style={styles.setBrowseCountWish}>{wishCount}/{totalCount}</span>
                  </div>
                </div>
              </div>
            );
          })}

        </div>
      </div>
    );
  }, [allSetsLoading, allSets, cards, wishListItems, cardsSetsFilter, cardsSetsSort, cardsSetsZoom, searchQuery, setsDisplayLimit, styles, theme]);

  // Set Detail View - when drilling into a set from sets view (shows ALL cards from TCGdex)
  const SetDetailView = () => {
    const selectedSetInfo = allSets.find(s => s.id === selectedCardSet);
    const setName = selectedSetInfo?.name || 'Set';
    const totalCount = selectedSetInfo?.cardCount?.total || selectedSetInfo?.cardCount?.official || '?';

    // Check owned/wish status for each card
    const isCardOwned = (cardId) => cards.some(c => c.card_id === `${selectedCardSet}-${cardId}`);
    const isCardWished = (cardId) => wishListItems.some(w => w.tcgdex_card_id === `${selectedCardSet}-${cardId}`);

    // Get owned card data for navigation to actual card detail
    const getOwnedCardData = (localId) => cards.find(c => c.card_id === `${selectedCardSet}-${localId}`);

    // Count for each filter
    const ownedCount = tcgSetCards.filter(c => isCardOwned(c.localId)).length;
    const wishCount = tcgSetCards.filter(c => isCardWished(c.localId)).length;

    // Filter and sort cards based on selected filter and sort
    const filteredSetCards = tcgSetCards
      .filter(card => {
        // Search filter - check card name and localId
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          (card.name || '').toLowerCase().includes(query) ||
          (card.localId || '').toLowerCase().includes(query)
        );
      })
      .filter(card => {
        if (setCardFilter === 'owned') return isCardOwned(card.localId);
        if (setCardFilter === 'wish') return isCardWished(card.localId);
        return true; // 'all'
      })
      .sort((a, b) => {
        switch (setCardsSort) {
          case 'number-desc':
            return (b.localId || '').localeCompare(a.localId || '', undefined, { numeric: true });
          case 'name-asc':
            return (a.name || '').localeCompare(b.name || '');
          case 'name-desc':
            return (b.name || '').localeCompare(a.name || '');
          case 'number-asc':
          default:
            return (a.localId || '').localeCompare(b.localId || '', undefined, { numeric: true });
        }
      });

    // Get dynamic count display based on filter
    const getCountDisplay = () => {
      if (setCardFilter === 'all') return totalCount;
      if (setCardFilter === 'owned') return `${ownedCount}/${totalCount}`;
      if (setCardFilter === 'wish') return `${wishCount}/${totalCount}`;
      return filteredSetCards.length;
    };

    const gridCols = cardZoom === 0 ? 4 : cardZoom === 1 ? 3 : 2;

    return (
      <div style={styles.viewContainer}>
        {/* Set Header - matches wishlist pattern */}
        <div style={styles.setDetailHeaderSingle}>
          <button
            style={styles.backButtonPremium}
            onClick={() => {
              setSelectedCardSet(null);
              setSetCardFilter('all');
            }}
          >
            <BackIcon />
          </button>
          <div style={styles.setFilterChips}>
            {['all', 'owned', 'wish'].map((filter) => (
              <button
                key={filter}
                style={{
                  ...styles.setFilterChip,
                  ...(setCardFilter === filter ? styles.setFilterChipActive : {}),
                }}
                onClick={() => setSetCardFilter(filter)}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
          <span style={{
            ...styles.setDetailCount,
            color: setCardFilter === 'wish' ? theme.warning : theme.accent,
          }}>
            {getCountDisplay()}
          </span>
          <button
            style={{...styles.setDetailSortButton, backgroundColor: theme.bgCard, borderColor: theme.border, color: theme.textSecondary}}
            onClick={() => setSortFilterModalOpen(true)}
          >
            <SortIcon />
          </button>
        </div>

        {/* Cards Grid */}
        {tcgSetCardsLoading ? (
          <div style={styles.wishListEmpty}>
            <div style={styles.spinner} />
            <span style={{ color: theme.textSecondary, marginTop: '12px' }}>Loading cards...</span>
          </div>
        ) : (
          <div style={{ ...styles.setCardsGrid, gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
            {filteredSetCards.map((card) => {
              const owned = isCardOwned(card.localId);
              const wished = isCardWished(card.localId);
              const ownedCardData = getOwnedCardData(card.localId);

              return (
                <div
                  key={card.id}
                  style={{
                    ...styles.setCardItem,
                    opacity: owned ? 1 : 0.5,
                  }}
                  onClick={() => {
                    // Set navigation context for prev/next in CardDetail
                    if (owned && ownedCardData) {
                      // Navigate to owned card
                      const visibleCards = filteredSetCards.filter(c => isCardOwned(c.localId));
                      const cardIds = visibleCards.map(c => getOwnedCardData(c.localId)?.id).filter(Boolean);
                      const currentIndex = cardIds.indexOf(ownedCardData.id);
                      setCardNavigationContext({
                        type: 'collection-set',
                        cardIds,
                        currentIndex: currentIndex >= 0 ? currentIndex : 0,
                        sourceLabel: setName,
                      });
                      navigate(`/card/${ownedCardData.id}`);
                    } else {
                      // Navigate to TCGdex card (for wish list viewing)
                      const cardIds = filteredSetCards.map(c => `tcg_${selectedCardSet}_${c.localId}`);
                      const currentIndex = filteredSetCards.findIndex(c => c.id === card.id);
                      setCardNavigationContext({
                        type: 'collection-set',
                        cardIds,
                        currentIndex: currentIndex >= 0 ? currentIndex : 0,
                        sourceLabel: setName,
                      });
                      navigate(`/card/tcg_${selectedCardSet}_${card.localId}`);
                    }
                  }}
                >
                  <div style={styles.setCardImgWrapper}>
                    <CardImage
                      digitalImage={card.image ? `${card.image}/low.webp` : `https://assets.tcgdex.net/en/${selectedCardSet}/${card.localId}/low.webp`}
                      name={card.name}
                      number={card.localId}
                      setName={card.set?.name}
                      imgStyle={styles.setCardImg}
                      theme={theme}
                      loading="lazy"
                    />
                    {/* Status indicators */}
                    {(owned || wished) && (
                      <div style={styles.setCardStatus}>
                        {owned && <span style={styles.setCardStatusOwned}>O</span>}
                        {wished && <span style={styles.setCardStatusWish}>W</span>}
                      </div>
                    )}
                  </div>
                  {cardZoom >= 2 && (
                    <span style={styles.setCardName}>{card.name}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!tcgSetCardsLoading && filteredSetCards.length === 0 && (
          <div style={styles.emptyState}>
            <span style={styles.emptyText}>
              {setCardFilter === 'owned' ? 'No owned cards from this set' :
               setCardFilter === 'wish' ? 'No wished cards from this set' :
               'No cards found'}
            </span>
          </div>
        )}
      </div>
    );
  };

  // Binder Detail View
  const BinderDetailView = () => (
    <div style={styles.viewContainer}>
      {/* Binder Header */}
      <div style={styles.binderDetailHeader}>
        <div style={styles.binderDetailTopRow}>
          <button style={styles.backButtonPremium} onClick={handleBackFromBinder}>
            <BackIcon />
          </button>
          <h2 style={styles.binderDetailTitle}>{selectedBinder?.name}</h2>
          <div style={styles.binderDetailImageWrapper}>
            <img
              src={binderImages[selectedBinder?.image] || binderImages.blue}
              alt={selectedBinder?.name}
              style={styles.binderDetailImage}
            />
          </div>
        </div>
        <span style={styles.binderDetailCount}>
          {getBinderCardCount(selectedBinder?.id)} cards
          {pricingEnabled && getBinderValue(selectedBinder?.id) && (
            <span style={{ marginLeft: '8px', color: '#4ade80', fontWeight: '600' }}>{getBinderValue(selectedBinder?.id)}</span>
          )}
        </span>
      </div>

      {/* Cards in Binder */}
      <LayoutGroup>
        <motion.div
          layout
          style={{
            ...styles.cardsGrid,
            gridTemplateColumns: cardZoom === 2
              ? 'repeat(auto-fill, minmax(140px, 1fr))'
              : cardZoom === 1
              ? 'repeat(auto-fill, minmax(105px, 1fr))'
              : 'repeat(auto-fill, minmax(75px, 1fr))',
            gap: cardZoom === 2 ? '12px' : cardZoom === 1 ? '10px' : '8px',
          }}
          transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* Add Card to Binder Placeholder */}
          {!isViewingOther && (
            <motion.div
              layout
              style={{
                ...styles.addCardPlaceholder,
                ...(cardZoom === 0 ? styles.addCardPlaceholderSmall : {}),
              }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              onClick={() => setAddToBinderModalOpen(true)}
            >
              <div style={styles.addCardInner}>
                <PlusIcon />
                <AnimatePresence>
                  {cardZoom >= 1 && (
                    <motion.span
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      style={styles.addCardText}
                    >
                      Add Card
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {getFilteredAndSortedCards(cards.filter(c => c.binder_id === selectedBinder?.id)).map((card) => (
            <motion.div
              key={card.id}
              layout
              style={styles.cardItem}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
              onClick={() => handleCardClick(card)}
            >
              <div style={styles.cardImageWrapper}>
                <CardImage
                  digitalImage={card.digital_image_url}
                  userPhoto={card.original_image_url}
                  name={card.name}
                  number={card.number}
                  setName={card.set_name}
                  preferPhoto={imageFilter === 'photo'}
                  imgStyle={styles.cardImage}
                  theme={theme}
                  loading="lazy"
                />
                {/* Price badge - bottom right */}
                {pricingEnabled && cardPrices[card.id] && (
                  <div style={{
                    ...styles.cardBadge,
                    ...styles.cardBadgeBottomRight,
                    ...(cardZoom === 0 ? styles.cardBadgeSmall : {}),
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    color: '#4ade80',
                  }}>
                    <span style={styles.cardBadgeText}>
                      {cardPrices[card.id].formatted}
                    </span>
                  </div>
                )}
                {card.is_private && (
                  <div style={{
                    ...styles.privateIndicator,
                    ...(cardZoom === 0 ? styles.privateIndicatorSmall : cardZoom === 2 ? styles.privateIndicatorLarge : {}),
                  }}>
                    <LockIcon size={cardZoom === 0 ? 10 : cardZoom === 2 ? 14 : 12} />
                  </div>
                )}
              </div>
              <AnimatePresence>
                {cardZoom >= 1 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    style={styles.cardInfo}
                    transition={{ duration: 0.25 }}
                  >
                    <span style={styles.cardName}>{card.name}</span>
                    {cardZoom === 2 && <span style={styles.cardGrade}>{card.grade}</span>}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </LayoutGroup>
    </div>
  );

  // Safety: force initialLoading off after 5s to prevent stuck spinner
  useEffect(() => {
    if (!initialLoading) return;
    const timeout = setTimeout(() => {
      console.warn('[Collection] Force-clearing initialLoading after timeout');
      setInitialLoading(false);
    }, 5000);
    return () => clearTimeout(timeout);
  }, [initialLoading]);

  // Show loading screen during initial load (prevents flash when auto-selecting binder)
  if (initialLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
        </div>
      </div>
    );
  }

  // Guest mode: only block when viewing OWN collection (/cards). Viewing another
  // user's collection (/user/:userId/collection) stays browsable.
  if (!user && isGuest() && !viewUserId) {
    return (
      <div style={{ ...styles.container, backgroundColor: theme.bg, minHeight: '100vh' }}>
        <GuestEmptyState
          title="Sign in to build your collection"
          body="Create an account to scan cards into binders, build your trade reputation, and connect with traders nearby."
          action="Create Account"
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Add Card/Binder Tutorial - Step 2: Point to Add button */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.ADD_CARD_BINDER_BUTTONS}
        spotlightMode="bottom-nav-add"
        message={[
          "Next, tap (+ Add) and",
          "select your option!",
          "",
          "Look for the (i) if you",
          "need more help!",
        ]}
      />

      {/* Backdrop for congrats overlays */}
      <TutorialBackdrop
        visible={
          currentStep === TUTORIAL_STEPS.FIRST_CARD_CONGRATS ||
          currentStep === TUTORIAL_STEPS.FIRST_BINDER_CONGRATS
        }
      />

      {/* Congrats after adding first card */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.FIRST_CARD_CONGRATS}
        mode="chase-congrats"
        message={[
          "Nice work! You just added",
          "your first card!",
          "",
          "Keep building your",
          "collection!",
        ]}
        onContinue={handleCongratsComplete}
        continueText="Got it!"
        onSecondary={() => {
          handleMoreTutorials();
          navigate('/home');
        }}
        secondaryText="More Tutorials"
        onClose={handleCongratsComplete}
      />

      {/* Congrats after adding first binder */}
      <TutorialOverlay
        visible={currentStep === TUTORIAL_STEPS.FIRST_BINDER_CONGRATS}
        mode="chase-congrats"
        message={[
          "Awesome! You just created",
          "your first binder!",
          "",
          "Now add some cards",
          "to organize!",
        ]}
        onContinue={handleCongratsComplete}
        continueText="Got it!"
        onSecondary={() => {
          handleMoreTutorials();
          navigate('/home');
        }}
        secondaryText="More Tutorials"
        onClose={handleCongratsComplete}
      />

      {/* Header */}
      <div style={styles.header}>
        {isViewingOther && (
          <button style={styles.headerBackBtn} onClick={() => {
            if (location.state?.from) {
              navigate(location.state.from);
            } else {
              navigate(-1);
            }
          }}>
            <BackIcon />
          </button>
        )}
        <div style={{ ...(isViewingOther ? { textAlign: 'center', flex: 1 } : {}) }}>
          <h1 style={{ ...styles.headerTitle, margin: 0 }}>
            {isViewingOther
              ? (profile?.username ? `@${profile.username}` : 'Trainer')
              : 'Collection'
            }
          </h1>
          {isViewingOther && distanceToUser !== null && (
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
              {distanceToUser < 5 ? '< 5' : distanceToUser} mi away
            </span>
          )}
        </div>
        <div style={styles.headerButtons}>
          {/* Wish List Match Indicator - only when viewing others and any matches exist */}
          {isViewingOther && (wishListMatches.length > 0 || reverseMatches.length > 0) && (
            <button
              style={{
                ...styles.matchIndicatorButton,
                // Special glow for mutual matches (both directions have cards)
                ...(wishListMatches.length > 0 && reverseMatches.length > 0 ? styles.mutualMatchButton : {})
              }}
              onClick={() => setMatchModalOpen(true)}
            >
              <MatchBoltIcon />
              <span style={styles.matchBadge}>{wishListMatches.length + reverseMatches.length}</span>
            </button>
          )}
          <button style={styles.iconButton} onClick={() => setShowStatsPanel(true)}>
            <ActivityIcon />
          </button>
          {!isViewingOther && pricingEnabled && (
            <button
              style={{...styles.iconButton, opacity: refreshCooldown ? 0.3 : 1}}
              onClick={handleRefreshPrices}
              disabled={refreshCooldown || refreshing}
              title={lastCollectionRefresh ? `Updated ${daysSinceRefresh(lastCollectionRefresh)}` : 'Refresh prices'}
            >
              <RefreshIcon />
            </button>
          )}
          {!isViewingOther && (
            <button style={styles.iconButton} onClick={() => navigate('/profile')}>
              <UserIcon />
            </button>
          )}
        </div>
        <div style={styles.headerBorder} />
      </div>

      {/* Refresh Progress Bar */}
      {(refreshing || autoFetching) && refreshProgress.total > 0 && (
        <div style={{
          width: '100%',
          height: '3px',
          backgroundColor: 'rgba(255,255,255,0.1)',
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          <div style={{
            width: `${(refreshProgress.done / refreshProgress.total) * 100}%`,
            height: '100%',
            backgroundColor: theme.accent,
            transition: 'width 0.3s ease',
          }} />
        </div>
      )}

      {/* Tab Switcher */}
      <div style={styles.tabWrapper}>
        <div style={styles.tabContainer}>
          <div
            style={{
              ...styles.tabIndicator,
              ...styles.tabIndicator3Tabs,
              transform: activeTab === 'cards'
                ? 'translateX(0)'
                : activeTab === 'binders'
                  ? 'translateX(100%)'
                  : 'translateX(200%)',
            }}
          />
          <button
            style={{ ...styles.tab, ...(activeTab === 'cards' ? styles.tabActive : {}) }}
            onClick={() => { setActiveTab('cards'); setSelectedBinder(null); }}
          >
            <CardsIcon />
            <span>Cards</span>
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'binders' ? styles.tabActive : {}) }}
            onClick={() => { setActiveTab('binders'); setSelectedBinder(null); }}
          >
            <BindersIcon />
            <span>Binders</span>
          </button>
          <button
            style={{ ...styles.tab, ...(activeTab === 'wishlist' ? styles.tabActive : {}) }}
            onClick={() => { setActiveTab('wishlist'); setSelectedBinder(null); }}
          >
            <WishListIcon />
            <span>Wish</span>
          </button>
        </div>
      </div>

      {/* Global Toolbar */}
      <div style={styles.globalToolbar}>
        {/* Search */}
        <div style={styles.searchContainer}>
          <SearchIcon />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => {
              // Delay blur to allow chip clicks
              setTimeout(() => {
                if (!searchQuery) setSearchFocused(false);
              }, 150);
            }}
            style={styles.searchInput}
          />
          {(searchQuery || searchFocused) && (
            <button
              style={styles.searchClearButton}
              onClick={() => {
                setSearchQuery('');
                setSearchFocused(false);
                searchInputRef.current?.blur();
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Toolbar buttons */}
        <motion.div
          style={styles.toolbarButtons}
          animate={{
            opacity: searchFocused ? 0 : 1,
            width: searchFocused ? 0 : 'auto',
            marginLeft: searchFocused ? 0 : undefined,
          }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        >
          {activeTab === 'cards' && !selectedBinder && !selectedCardSet && (
            <button
              style={styles.toolButton}
              onClick={() => {
                setCardsViewMode(prev => prev === 'cards' ? 'sets' : 'cards');
                setSelectedCardSet(null);
              }}
            >
              {cardsViewMode === 'sets' ? <SetsViewIcon /> : <CardsViewIcon />}
            </button>
          )}
          {(activeTab === 'cards' || selectedBinder) && (
            <button
              style={{
                ...styles.toolButton,
                ...((cardsViewMode === 'sets' && activeTab === 'cards' ? cardsSetsZoom : cardZoom) > 0 ? styles.toolButtonActive : {}),
              }}
              onClick={() => {
                if (cardsViewMode === 'sets' && activeTab === 'cards' && !selectedBinder) {
                  setCardsSetsZoom((prev) => (prev + 1) % 3);
                } else {
                  setCardZoom((prev) => (prev + 1) % 3);
                }
              }}
            >
              {(cardsViewMode === 'sets' && activeTab === 'cards' ? cardsSetsZoom : cardZoom) === 2 ? <ZoomOutIcon /> : <ZoomInIcon />}
            </button>
          )}
          {activeTab === 'wishlist' && (
            <button
              style={{
                ...styles.toolButton,
                ...(wishListZoom > 0 ? styles.toolButtonActive : {}),
              }}
              onClick={() => setWishListZoom((prev) => (prev + 1) % 3)}
            >
              {wishListZoom === 2 ? <ZoomOutIcon /> : <ZoomInIcon />}
            </button>
          )}
          <button
            style={styles.toolButton}
            onClick={() => setSortFilterModalOpen(true)}
          >
            <SortIcon />
          </button>
          {!isViewingOther && (activeTab === 'cards' || selectedBinder) && (
            <button
              style={{
                ...styles.toolButton,
                ...(bulkEditMode ? styles.toolButtonActive : {}),
              }}
              onClick={() => setBulkEditModalOpen(true)}
            >
              <EditIcon />
            </button>
          )}
        </motion.div>
      </div>

      {/* Search Filter Chips */}
      {searchQuery.length >= 2 && (
        <div style={styles.searchFilterChips}>
          <button
            style={{
              ...styles.searchFilterChip,
              backgroundColor: activeTab === 'cards' && cardsViewMode === 'cards' ? 'var(--purple)' : 'var(--bg-card)',
              color: activeTab === 'cards' && cardsViewMode === 'cards' ? '#fff' : 'var(--text)',
            }}
            onClick={() => {
              setActiveTab('cards');
              setCardsViewMode('cards');
              setSelectedCardSet(null);
              setSelectedBinder(null);
              searchInputRef.current?.blur();
            }}
          >
            My Cards ({cards.filter(c =>
              c.card_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              c.name?.toLowerCase().includes(searchQuery.toLowerCase())
            ).length})
          </button>
          <button
            style={{
              ...styles.searchFilterChip,
              backgroundColor: activeTab === 'wishlist' ? 'var(--purple)' : 'var(--bg-card)',
              color: activeTab === 'wishlist' ? '#fff' : 'var(--text)',
            }}
            onClick={() => {
              setActiveTab('wishlist');
              setSelectedWishListSet(null);
              setSelectedBinder(null);
              searchInputRef.current?.blur();
            }}
          >
            Wish List ({wishListItems.filter(w =>
              w.card_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
              w.name?.toLowerCase().includes(searchQuery.toLowerCase())
            ).length})
          </button>
          <button
            style={{
              ...styles.searchFilterChip,
              backgroundColor: activeTab === 'cards' && cardsViewMode === 'sets' && !selectedCardSet ? 'var(--purple)' : 'var(--bg-card)',
              color: activeTab === 'cards' && cardsViewMode === 'sets' && !selectedCardSet ? '#fff' : 'var(--text)',
            }}
            onClick={() => {
              setActiveTab('cards');
              setCardsViewMode('sets');
              setSelectedCardSet(null);
              setSelectedBinder(null);
              searchInputRef.current?.blur();
            }}
          >
            Sets ({allSets.filter(s => s.name?.toLowerCase().includes(searchQuery.toLowerCase())).length})
          </button>
        </div>
      )}

      {/* Main Content */}
      <div style={styles.content} onScroll={handleContentScroll}>
        <AnimatePresence mode="wait">
          {selectedBinder ? (
            <motion.div
              key="binder-detail"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <BinderDetailView />
            </motion.div>
          ) : activeTab === 'cards' ? (
            selectedCardSet ? (
              <motion.div
                key="set-detail"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <SetDetailView />
              </motion.div>
            ) : cardsViewMode === 'sets' ? (
              <motion.div
                key="cards-sets"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {cardsSetsView}
              </motion.div>
            ) : (
              <motion.div
                key="cards"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {cardsView}
              </motion.div>
            )
          ) : activeTab === 'binders' ? (
            <motion.div
              key="binders"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {bindersView}
            </motion.div>
          ) : (
            <motion.div
              key="wishlist"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {wishListView}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Stats Panel */}
      <AnimatePresence>
        {showStatsPanel && <StatsPanel />}
      </AnimatePresence>

      {/* All Badges Modal */}
      <AllBadgesModal
        isOpen={allBadgesOpen}
        onClose={() => setAllBadgesOpen(false)}
        badgeData={badgeData}
        theme={theme}
      />

      {/* Bulk Edit Floating Action Bar */}
      <AnimatePresence>
        {bulkEditMode && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={styles.bulkEditActionBar}
          >
            <button style={styles.bulkEditCancelAction} onClick={cancelBulkEdit}>
              Cancel
            </button>
            <div style={styles.bulkEditActionInfo}>
              <span style={styles.bulkEditActionLabel}>
                {bulkEditMode === 'tradeable' ? 'Mark Tradeable'
                  : bulkEditMode === 'pc' ? 'Mark PC'
                  : bulkEditMode === 'public' ? 'Mark Public'
                  : bulkEditMode === 'private' ? 'Mark Private'
                  : 'Delete'}
              </span>
              <span style={styles.bulkEditActionCount}>
                {selectedCards.length} selected
              </span>
            </div>
            <button
              style={{
                ...styles.bulkEditApplyAction,
                opacity: selectedCards.length === 0 ? 0.5 : 1,
              }}
              onClick={handleBulkEditApply}
              disabled={selectedCards.length === 0}
            >
              {bulkEditMode === 'delete' ? `Delete (${selectedCards.length})` : 'Apply'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirm Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'fixed',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
              }}
              onClick={() => !deleteLoading && setShowDeleteConfirm(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                onClick={e => e.stopPropagation()}
                style={{
                  backgroundColor: theme === 'dark' ? '#1a1a2e' : '#ffffff',
                  borderRadius: '20px',
                  padding: '28px 24px',
                  width: '100%',
                  maxWidth: '340px',
                  textAlign: 'center',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}
              >
                <div style={{
                  width: '48px', height: '48px', borderRadius: '50%',
                  backgroundColor: 'rgba(220, 38, 38, 0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </div>
                <h3 style={{
                  fontSize: '18px', fontWeight: 600, margin: '0 0 8px',
                  color: theme === 'dark' ? '#ffffff' : '#1a1a1a',
                }}>
                  Delete {selectedCards.length} card{selectedCards.length !== 1 ? 's' : ''}?
                </h3>
                <p style={{
                  fontSize: '14px', color: theme === 'dark' ? '#999' : '#666',
                  margin: '0 0 24px', lineHeight: 1.5,
                }}>
                  This will remove {selectedCards.length === 1 ? 'this card' : 'these cards'} from your collection. This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleteLoading}
                    style={{
                      flex: 1, padding: '14px', fontSize: '15px', fontWeight: 500,
                      border: 'none', borderRadius: '12px', cursor: 'pointer',
                      backgroundColor: theme === 'dark' ? '#2a2a3e' : '#f1f5f9',
                      color: theme === 'dark' ? '#ccc' : '#333',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    disabled={deleteLoading}
                    style={{
                      flex: 1, padding: '14px', fontSize: '15px', fontWeight: 600,
                      border: 'none', borderRadius: '12px', cursor: 'pointer',
                      backgroundColor: '#dc2626', color: '#ffffff',
                      opacity: deleteLoading ? 0.7 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    }}
                  >
                    {deleteLoading ? (
                      <>
                        <div style={{
                          width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)',
                          borderTopColor: '#ffffff', borderRadius: '50%',
                          animation: 'spin 0.6s linear infinite',
                        }} />
                        Deleting...
                      </>
                    ) : 'Delete'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Report User Modal */}
      <ReportUserModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        userId={viewUserId}
        currentUserId={currentUserId}
        onSuccess={handleReportSuccess}
      />

      {/* Add Card Modal (from Cards tab) */}
      <AddCardModal
        isOpen={addCardModalOpen}
        onClose={() => setAddCardModalOpen(false)}
        binders={binders}
        onAddCard={handleAddCard}
      />

      {/* Add Binder Modal (from Binders tab) */}
      <AddBinderModal
        isOpen={addBinderModalOpen}
        onClose={() => setAddBinderModalOpen(false)}
        existingBinders={binders}
        onCreateBinder={handleCreateBinder}
      />

      {/* Add to Binder Modal (from inside binder) */}
      <AddToBinderModal
        isOpen={addToBinderModalOpen}
        onClose={() => setAddToBinderModalOpen(false)}
        binder={selectedBinder}
        allCards={cards}
        binders={binders}
        onAddNewCard={handleAddNewCardToBinder}
        onMoveCards={handleMoveCardsToBinder}
      />

      {/* Old scan modals removed - now using /scanner page */}

      {/* Sort/Filter Modal */}
      <SortFilterModal
        isOpen={sortFilterModalOpen}
        onClose={() => setSortFilterModalOpen(false)}
        mode={
          selectedBinder ? 'binder-detail'
          : activeTab === 'binders' ? 'binders'
          : activeTab === 'wishlist' ? (selectedWishListSet ? 'wishlist-cards' : 'wishlist-sets')
          : activeTab === 'cards' && cardsViewMode === 'sets' && selectedCardSet ? 'set-cards'
          : activeTab === 'cards' && cardsViewMode === 'sets' ? 'cards-sets'
          : 'cards'
        }
        isOwnCollection={!isViewingOther}
        tradeFilter={tradeFilter}
        setTradeFilter={setTradeFilter}
        visibilityFilter={visibilityFilter}
        setVisibilityFilter={setVisibilityFilter}
        imageFilter={imageFilter}
        setImageFilter={setImageFilter}
        sortBy={
          activeTab === 'cards' && cardsViewMode === 'sets' && selectedCardSet ? setCardsSort
          : activeTab === 'cards' && cardsViewMode === 'sets' ? cardsSetsSort
          : activeTab === 'wishlist' ? wishListSortBy
          : selectedBinder || activeTab === 'cards' ? cardSortBy
          : binderSortBy
        }
        setSortBy={
          activeTab === 'cards' && cardsViewMode === 'sets' && selectedCardSet ? setSetCardsSort
          : activeTab === 'cards' && cardsViewMode === 'sets' ? setCardsSetsSort
          : activeTab === 'wishlist' ? setWishListSortBy
          : selectedBinder || activeTab === 'cards' ? setCardSortBy
          : setBinderSortBy
        }
        setsFilter={cardsSetsFilter}
        setSetsFilter={setCardsSetsFilter}
        setsLanguage={setsLanguage}
        setSetsLanguage={setSetsLanguage}
        onReset={resetPreferences}
      />

      {/* Wish List Search Modal */}
      <WishListSearchModal
        isOpen={wishListSearchOpen}
        onClose={() => setWishListSearchOpen(false)}
        userId={profile?.id}
        wishListCount={wishListCount}
        priorityCount={wishListItems.filter(item => item.is_priority === true).length}
        onSuccess={refreshWishList}
      />

      {/* Wish List Match Modal */}
      <WishListMatchModal
        isOpen={matchModalOpen}
        onClose={() => setMatchModalOpen(false)}
        theyHaveMatches={wishListMatches}
        youHaveMatches={reverseMatches}
        ownerUsername={profile?.username}
        ownerId={viewUserId}
      />

      {/* Wish List Card Menu (long-press) */}
      <WishListCardMenu
        isOpen={wishCardMenuOpen}
        onClose={() => {
          setWishCardMenuOpen(false);
          setSelectedWishCard(null);
        }}
        card={selectedWishCard}
        priorityCount={wishListItems.filter(item => item.is_priority === true).length}
        onUpdate={refreshWishList}
        showToast={showToast}
      />

      {/* Toast Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={{
              ...styles.toast,
              backgroundColor: toastType === 'warning' ? theme.warning : toastType === 'error' ? theme.error : theme.accent,
              color: toastType === 'warning' ? '#000' : '#fff',
            }}
          >
            {toastMessage}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Edit Modal */}
      <AnimatePresence>
        {bulkEditModalOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={styles.bulkEditBackdrop}
              onClick={() => setBulkEditModalOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={styles.bulkEditModal}
            >
              <div style={styles.bulkEditHandle} />
              <div style={styles.bulkEditHeader}>
                <h2 style={styles.bulkEditTitle}>Bulk Edit Cards</h2>
                <button
                  style={styles.bulkEditCloseBtn}
                  onClick={() => setBulkEditModalOpen(false)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18" />
                    <path d="M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p style={styles.bulkEditSubtitle}>Select an action to apply to multiple cards</p>
              <div style={styles.bulkEditOptions}>
                <button
                  style={styles.bulkEditOption}
                  onClick={() => {
                    setBulkEditMode('tradeable');
                    setBulkEditModalOpen(false);
                  }}
                >
                  <div style={{ ...styles.bulkEditOptionIcon, backgroundColor: `${theme.success}26`, color: theme.success }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 3h5v5M8 3H3v5M3 16v5h5M21 16v5h-5M7 12l3 3 7-7" />
                    </svg>
                  </div>
                  <div style={styles.bulkEditOptionInfo}>
                    <span style={styles.bulkEditOptionTitle}>Mark as Tradeable</span>
                    <span style={styles.bulkEditOptionDesc}>Select cards available for trade</span>
                  </div>
                </button>
                <button
                  style={styles.bulkEditOption}
                  onClick={() => {
                    setBulkEditMode('pc');
                    setBulkEditModalOpen(false);
                  }}
                >
                  <div style={{ ...styles.bulkEditOptionIcon, backgroundColor: `${theme.accent}26`, color: theme.accent }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                    </svg>
                  </div>
                  <div style={styles.bulkEditOptionInfo}>
                    <span style={styles.bulkEditOptionTitle}>Mark as PC</span>
                    <span style={styles.bulkEditOptionDesc}>Select cards for personal collection</span>
                  </div>
                </button>
                <button
                  style={styles.bulkEditOption}
                  onClick={() => {
                    setBulkEditMode('public');
                    setBulkEditModalOpen(false);
                  }}
                >
                  <div style={{ ...styles.bulkEditOptionIcon, backgroundColor: `${theme.purple}26`, color: theme.purple }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </div>
                  <div style={styles.bulkEditOptionInfo}>
                    <span style={styles.bulkEditOptionTitle}>Mark as Public</span>
                    <span style={styles.bulkEditOptionDesc}>Make cards visible to others</span>
                  </div>
                </button>
                <button
                  style={styles.bulkEditOption}
                  onClick={() => {
                    setBulkEditMode('private');
                    setBulkEditModalOpen(false);
                  }}
                >
                  <div style={{ ...styles.bulkEditOptionIcon, backgroundColor: `${theme.textSecondary}26`, color: theme.textSecondary }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                      <line x1="2" x2="22" y1="2" y2="22" />
                    </svg>
                  </div>
                  <div style={styles.bulkEditOptionInfo}>
                    <span style={styles.bulkEditOptionTitle}>Mark as Private</span>
                    <span style={styles.bulkEditOptionDesc}>Hide cards from others</span>
                  </div>
                </button>
                <button
                  style={{ ...styles.bulkEditOption, borderColor: 'rgba(239, 68, 68, 0.2)' }}
                  onClick={() => {
                    setBulkEditMode('delete');
                    setBulkEditModalOpen(false);
                  }}
                >
                  <div style={{ ...styles.bulkEditOptionIcon, backgroundColor: `${theme.error}26`, color: theme.error }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18" />
                      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                    </svg>
                  </div>
                  <div style={styles.bulkEditOptionInfo}>
                    <span style={{ ...styles.bulkEditOptionTitle, color: theme.error }}>Delete Cards</span>
                    <span style={styles.bulkEditOptionDesc}>Select cards to remove</span>
                  </div>
                </button>
              </div>
              {bulkEditMode && (
                <button
                  style={styles.bulkEditCancelBtn}
                  onClick={() => {
                    setBulkEditMode(null);
                    setSelectedCards([]);
                    setBulkEditModalOpen(false);
                  }}
                >
                  Cancel Selection Mode
                </button>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const getStyles = (theme) => ({
  container: {
    width: '100%',
    height: '100%',
    backgroundColor: theme.bg,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: theme.bg,
    flexShrink: 0,
    position: 'relative',
  },
  headerBorder: {
    position: 'absolute',
    bottom: 0,
    left: '40px',
    right: '40px',
    height: '2px',
    backgroundColor: 'rgba(255, 255, 255, 0.35)',
    borderRadius: '1px',
  },
  headerTitle: {
    color: theme.text,
    fontSize: '20px',
    fontWeight: '700',
    margin: 0,
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  headerBackBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.text,
    flexShrink: 0,
  },
  iconButton: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
    transition: 'all 0.15s ease',
  },
  matchIndicatorButton: {
    position: 'relative',
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: 'rgba(250, 204, 21, 0.15)',
    border: `2px solid ${theme.warning}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.warning,
    animation: 'matchPulse 2s ease-in-out infinite',
    boxShadow: '0 0 20px rgba(250, 204, 21, 0.3)',
  },
  matchBadge: {
    position: 'absolute',
    top: '-6px',
    right: '-6px',
    minWidth: '20px',
    height: '20px',
    borderRadius: '10px',
    backgroundColor: theme.error,
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 5px',
    border: `2px solid ${theme.bg}`,
  },
  mutualMatchButton: {
    // Purple glow for mutual matches (you both have cards each other wants!)
    backgroundColor: 'rgba(139, 92, 246, 0.2)',
    border: `2px solid ${theme.purple}`,
    color: theme.purple,
    boxShadow: '0 0 25px rgba(139, 92, 246, 0.4)',
    animation: 'matchPulse 1.5s ease-in-out infinite',
  },
  menuWrapper: {
    position: 'relative',
  },
  tabWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px 16px 16px',
    backgroundColor: theme.bg,
  },
  tabContainer: {
    position: 'relative',
    display: 'flex',
    backgroundColor: theme.bgSheet,
    borderRadius: '16px',
    padding: '4px',
    border: `1px solid ${theme.border}`,
  },
  tabIndicator: {
    position: 'absolute',
    top: '4px',
    left: '4px',
    height: 'calc(100% - 8px)',
    backgroundColor: theme.accent,
    borderRadius: '12px',
    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 2px 8px rgba(14, 165, 233, 0.3)',
  },
  tabIndicator2Tabs: {
    width: 'calc(50% - 4px)',
  },
  tabIndicator3Tabs: {
    width: 'calc(33.333% - 4px)',
  },
  tab: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 24px',
    borderRadius: '12px',
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.textSecondary,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    minWidth: '120px',
  },
  tabActive: {
    color: '#fff',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    paddingBottom: '100px',
  },
  globalToolbar: {
    display: 'flex',
    gap: '12px',
    padding: '0 16px 16px',
    backgroundColor: theme.bg,
  },
  viewContainer: {
    padding: '0 16px',
  },
  searchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    backgroundColor: theme.bgSheet,
    borderRadius: '10px',
    border: `1px solid ${theme.border}`,
    color: theme.textSecondary,
    flex: 1,
    minWidth: 0,
  },
  toolbarButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    overflow: 'hidden',
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    backgroundColor: 'transparent',
    border: 'none',
    outline: 'none',
    color: theme.text,
    fontSize: '13px',
  },
  toolButton: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    backgroundColor: theme.bgSheet,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
    transition: 'all 0.2s ease',
  },
  toolButtonActive: {
    backgroundColor: theme.accent,
    borderColor: theme.accent,
    color: theme.text,
  },
  searchFilterChips: {
    display: 'flex',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: theme.bg,
    borderBottom: `1px solid ${theme.border}`,
    overflowX: 'auto',
  },
  searchFilterChip: {
    padding: '6px 12px',
    borderRadius: '16px',
    border: `1px solid ${theme.border}`,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'all 0.15s ease',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
  },
  addCardPlaceholder: {
    aspectRatio: '0.714',
    borderRadius: '12px',
    border: `2px dashed ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: theme.bgCard,
  },
  addCardPlaceholderSmall: {
    borderRadius: '8px',
  },
  addCardInner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    color: theme.accent,
  },
  addCardText: {
    fontSize: '12px',
    fontWeight: '600',
  },
  cardItem: {
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: theme.bgSheet,
    cursor: 'pointer',
  },
  cardImageWrapper: {
    position: 'relative',
    width: '100%',
  },
  cardImage: {
    width: '100%',
    aspectRatio: '0.714',
    objectFit: 'cover',
    display: 'block',
  },
  privateIndicator: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    width: '24px',
    height: '24px',
    borderRadius: '6px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.text,
  },
  privateIndicatorSmall: {
    top: '4px',
    right: '4px',
    width: '18px',
    height: '18px',
    borderRadius: '4px',
  },
  privateIndicatorLarge: {
    top: '8px',
    right: '8px',
    width: '28px',
    height: '28px',
    borderRadius: '8px',
  },
  cardInfo: {
    padding: '8px',
    textAlign: 'center',
  },
  cardName: {
    display: 'block',
    color: theme.text,
    fontSize: '12px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardGrade: {
    display: 'block',
    color: theme.textSecondary,
    fontSize: '11px',
    marginTop: '2px',
  },
  bindersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
    padding: '8px 0',
  },
  binderItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  binderImageWrapper: {
    position: 'relative',
    width: '100%',
    aspectRatio: '0.8',
    marginBottom: '8px',
  },
  binderImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.4))',
    transition: 'transform 0.3s ease, filter 0.3s ease',
  },
  binderGlow: {
    position: 'absolute',
    bottom: '-10px',
    left: '10%',
    right: '10%',
    height: '20px',
    background: 'radial-gradient(ellipse at center, rgba(14, 165, 233, 0.3) 0%, transparent 70%)',
    filter: 'blur(8px)',
  },
  addBinderPlaceholder: {
    width: '100%',
    aspectRatio: '0.8',
    borderRadius: '12px',
    border: `2px dashed ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
    color: theme.accent,
    backgroundColor: theme.bgCard,
  },
  binderName: {
    color: theme.text,
    fontSize: '13px',
    fontWeight: '600',
    textAlign: 'center',
  },
  binderCount: {
    color: theme.textSecondary,
    fontSize: '11px',
    marginTop: '2px',
  },
  binderValue: {
    color: '#4ade80',
    fontSize: '11px',
    fontWeight: '600',
    marginTop: '1px',
    display: 'block',
  },
  // Wish List Empty State
  wishListEmpty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 32px',
    textAlign: 'center',
    color: theme.textSecondary,
  },
  wishListEmptyTitle: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: '16px 0 8px',
  },
  wishListEmptyText: {
    color: theme.textSecondary,
    fontSize: '14px',
    lineHeight: '1.5',
    maxWidth: '280px',
    margin: '0 0 24px',
  },
  wishListAddButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: theme.accent,
    color: theme.text,
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  wishListCounter: {
    color: theme.textSecondary,
    fontSize: '12px',
    marginTop: '12px',
  },
  wishListToolbarCounter: {
    backgroundColor: `${theme.accent}26`,
    color: theme.accent,
    fontSize: '12px',
    fontWeight: '600',
    padding: '6px 10px',
    borderRadius: '12px',
    border: '1px solid rgba(14, 165, 233, 0.3)',
  },
  // Wish List Priority Sections
  wishPrioritySection: {
    margin: '8px 16px 16px',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid',
  },
  wishMoreSection: {
    margin: '0 16px 16px',
    padding: '16px',
    borderRadius: '16px',
    border: '1px solid',
  },
  wishSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  wishSectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  wishSectionTitle: {
    fontSize: '15px',
    fontWeight: '600',
  },
  wishSectionCount: {
    fontSize: '14px',
    fontWeight: '600',
  },
  wishSectionEmpty: {
    padding: '24px 16px',
    textAlign: 'center',
  },
  // Toast notification
  toast: {
    position: 'fixed',
    bottom: '100px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '12px 24px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    zIndex: 3000,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },
  // Sets Browse Grid (2-column layout)
  setsBrowseGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    padding: '8px 16px 16px',
  },
  setBrowseCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.bgSheet,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.15s ease, border-color 0.15s ease',
  },
  setBrowseLogo: {
    width: '100%',
    height: '60px',
    backgroundColor: theme.bgCard,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 12px',
    overflow: 'hidden',
  },
  setBrowseLogoImg: {
    width: 'auto',
    height: '44px',
    maxWidth: '100%',
    objectFit: 'contain',
  },
  setBrowseLogoFallback: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.textSecondary,
    fontSize: '24px',
    fontWeight: '700',
  },
  setBrowseLogoWishList: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(250, 204, 21, 0.15)',
    border: '1px solid rgba(250, 204, 21, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.warning,
  },
  setBrowseInfo: {
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  },
  setBrowseName: {
    color: theme.text,
    fontSize: '12px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center',
    maxWidth: '100%',
  },
  setBrowseCounts: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
  },
  setBrowseCountOwned: {
    color: theme.accent,
    fontSize: '11px',
    fontWeight: '600',
  },
  setBrowseCountWish: {
    color: theme.warning,
    fontSize: '11px',
    fontWeight: '600',
  },
  loadMoreCard: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backgroundColor: theme.bgCard,
    borderRadius: '12px',
    border: `1px dashed ${theme.border}`,
    cursor: 'pointer',
    gap: '4px',
  },
  loadMoreText: {
    color: theme.accent,
    fontSize: '14px',
    fontWeight: '600',
  },
  loadMoreCount: {
    color: theme.textSecondary,
    fontSize: '11px',
  },
  // Set Cards Grid (for set detail view)
  setCardsGrid: {
    display: 'grid',
    gap: '8px',
    padding: '8px 16px 16px',
    alignItems: 'start',
  },
  setCardItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    cursor: 'pointer',
  },
  setCardImgWrapper: {
    position: 'relative',
    width: '100%',
    height: 0,
    paddingBottom: '139.86%',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: theme.bgCard,
  },
  setCardImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '8px',
  },
  setCardStatus: {
    position: 'absolute',
    bottom: '4px',
    left: '4px',
    display: 'flex',
    gap: '4px',
  },
  setCardStatusOwned: {
    backgroundColor: `${theme.accent}e6`,
    color: '#fff',
    fontSize: '9px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  setCardStatusWish: {
    backgroundColor: 'rgba(250, 204, 21, 0.9)',
    color: '#000',
    fontSize: '9px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '4px',
  },
  setCardName: {
    color: theme.text,
    fontSize: '11px',
    fontWeight: '500',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  setCardNumber: {
    color: theme.textSecondary,
    fontSize: '10px',
  },
  // Wish List Sets Grid (legacy - keeping for compatibility)
  wishListSetsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    padding: '16px',
  },
  wishListSetCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 8px',
    backgroundColor: theme.bgSheet,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
    transition: 'transform 0.15s ease',
  },
  wishListSetLogo: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: theme.bgCard,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  wishListSetLogoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  wishListSetLogoFallback: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.textSecondary,
    fontSize: '18px',
    fontWeight: '600',
  },
  wishListSetLogoAll: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.purple} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.text,
  },
  wishListSetLogoAdd: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    backgroundColor: theme.bgCard,
    border: `2px dashed ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.textSecondary,
  },
  wishListSetName: {
    color: theme.text,
    fontSize: '11px',
    fontWeight: '600',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  wishListSetCount: {
    color: theme.accent,
    fontSize: '10px',
    fontWeight: '600',
  },
  // Wish List Cards View
  wishListCardsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.bgCard}`,
  },
  wishListCardsTitle: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    margin: 0,
    whiteSpace: 'nowrap',
  },
  setDetailHeaderSingle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.bgCard}`,
  },
  setFilterChips: {
    display: 'flex',
    gap: '6px',
    flex: 1,
    justifyContent: 'center',
  },
  setFilterChip: {
    padding: '6px 12px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.border}`,
    borderRadius: '14px',
    color: theme.textSecondary,
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  setFilterChipActive: {
    backgroundColor: `${theme.accent}26`,
    borderColor: theme.accent,
    color: theme.accent,
  },
  setDetailCount: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  setDetailSortButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    outline: 'none',
    WebkitTapHighlightColor: 'transparent',
  },
  wishListCardsCount: {
    color: theme.accent,
    fontSize: '13px',
    fontWeight: '600',
  },
  wishListCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    padding: '16px',
  },
  wishListCardItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    cursor: 'pointer',
  },
  wishListCardImage: {
    position: 'relative',
    width: '100%',
    aspectRatio: '0.714',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: theme.bgCard,
  },
  wishListCardImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  wishListVariantBadge: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    padding: '2px 6px',
    backgroundColor: 'rgba(139, 92, 246, 0.9)',
    color: theme.text,
    fontSize: '8px',
    fontWeight: '600',
    borderRadius: '4px',
  },
  wishListCardName: {
    color: theme.text,
    fontSize: '11px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  wishListCardSet: {
    color: theme.textSecondary,
    fontSize: '10px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: `3px solid ${theme.border}`,
    borderTopColor: theme.accent,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  binderDetailHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginBottom: '24px',
  },
  binderDetailTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  backButtonPremium: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.text,
    transition: 'all 0.2s ease',
    flexShrink: 0,
  },
  binderDetailImageWrapper: {
    flexShrink: 0,
  },
  binderDetailImage: {
    width: '50px',
    height: '62px',
    objectFit: 'contain',
    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))',
  },
  binderDetailTitle: {
    flex: 1,
    color: theme.text,
    fontSize: '18px',
    fontWeight: '700',
    margin: 0,
    textAlign: 'center',
  },
  binderDetailCount: {
    color: theme.textSecondary,
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'center',
    display: 'block',
  },
  setDetailCount: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 12px',
    backgroundColor: theme.accentBg,
    borderRadius: '8px',
    minWidth: '60px',
  },
  setDetailCountText: {
    color: theme.accent,
    fontSize: '14px',
    fontWeight: '600',
  },
  // Stats Panel Styles
  statsPanelOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  statsPanel: {
    backgroundColor: theme.bgCard,
    borderRadius: '20px',
    border: `1px solid ${theme.border}`,
    width: '100%',
    maxWidth: '400px',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  statsPanelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px',
    borderBottom: `1px solid ${theme.border}`,
  },
  statsPanelTitle: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '700',
    margin: 0,
  },
  statsPanelClose: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  userActionsSection: {
    display: 'flex',
    gap: '8px',
    padding: '0 20px 16px',
  },
  userActionButton: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: '10px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    cursor: 'pointer',
  },
  userActionButtonActive: {
    backgroundColor: `${theme.accent}26`,
    borderColor: theme.accent,
  },
  userActionButtonDanger: {
    flex: 1,
    padding: '10px 12px',
    borderRadius: '10px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    cursor: 'pointer',
  },
  userActionText: {
    color: theme.text,
    fontSize: '13px',
    fontWeight: '600',
  },
  userActionTextDanger: {
    color: theme.error,
    fontSize: '13px',
    fontWeight: '600',
  },
  quickStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    padding: '20px',
  },
  quickStatCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 8px',
    backgroundColor: theme.bgCard,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
  },
  quickStatIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: `${theme.accent}26`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '8px',
    color: theme.accent,
  },
  quickStatValue: {
    color: theme.text,
    fontSize: '20px',
    fontWeight: '700',
  },
  quickStatLabel: {
    color: theme.textSecondary,
    fontSize: '11px',
    fontWeight: '500',
    marginTop: '2px',
  },
  tradeReputationSection: {
    padding: '0 20px 20px',
  },
  sectionTitle: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    margin: '0 0 16px 0',
  },
  overallRating: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    backgroundColor: theme.bgCard,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
    marginBottom: '16px',
  },
  starsRow: {
    display: 'flex',
    gap: '4px',
  },
  ratingText: {
    color: theme.textSecondary,
    fontSize: '13px',
  },
  ratingBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  ratingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  ratingLabel: {
    color: theme.textSecondary,
    fontSize: '12px',
    width: '100px',
    flexShrink: 0,
  },
  ratingBarContainer: {
    flex: 1,
    height: '6px',
    backgroundColor: theme.border,
    borderRadius: '3px',
    overflow: 'hidden',
  },
  ratingBar: {
    height: '100%',
    backgroundColor: theme.accent,
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  ratingValue: {
    color: theme.text,
    fontSize: '12px',
    fontWeight: '600',
    width: '30px',
    textAlign: 'right',
  },
  noReviews: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px',
    backgroundColor: theme.bgCard,
    borderRadius: '12px',
    border: `1px solid ${theme.border}`,
  },
  noReviewsText: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
  },
  noReviewsSubtext: {
    color: theme.textSecondary,
    fontSize: '12px',
    marginTop: '4px',
    textAlign: 'center',
  },
  // Trade Activity styles
  tradeActivityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '8px',
    marginBottom: '12px',
  },
  tradeActivityItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '8px 4px',
    backgroundColor: theme.bgSheet,
    borderRadius: '8px',
  },
  tradeActivityValue: {
    fontSize: '18px',
    fontWeight: '700',
    marginBottom: '2px',
  },
  tradeActivityLabel: {
    fontSize: '9px',
    color: theme.textMuted,
    textTransform: 'uppercase',
  },
  activityIndicatorsRow: {
    display: 'flex',
    gap: '8px',
  },
  activityIndicator: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '10px 12px',
    backgroundColor: theme.bgSheet,
    borderRadius: '8px',
  },
  activityLabel: {
    fontSize: '10px',
    color: theme.textMuted,
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  activityValue: {
    fontSize: '14px',
    fontWeight: '700',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: `3px solid ${theme.border}`,
    borderTopColor: theme.accent,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  // Bulk Edit Modal
  bulkEditBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    zIndex: 1000,
  },
  bulkEditModal: {
    position: 'fixed',
    bottom: '100px',
    left: '8px',
    right: '8px',
    backgroundColor: theme.bgSheet,
    borderRadius: '20px',
    padding: '12px 20px 24px',
    zIndex: 1001,
    overflow: 'hidden',
    boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.5)',
    maxHeight: '65vh',
    overflowY: 'auto',
  },
  bulkEditHandle: {
    width: '36px',
    height: '4px',
    backgroundColor: theme.border,
    borderRadius: '2px',
    margin: '0 auto 16px',
  },
  bulkEditHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  bulkEditTitle: {
    color: theme.text,
    fontSize: '18px',
    fontWeight: '600',
    margin: 0,
  },
  bulkEditCloseBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: theme.textSecondary,
  },
  bulkEditSubtitle: {
    color: theme.textSecondary,
    fontSize: '13px',
    margin: '0 0 16px 0',
  },
  bulkEditOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  bulkEditOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px',
    backgroundColor: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
    WebkitTapHighlightColor: 'transparent',
  },
  bulkEditOptionIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bulkEditOptionInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  bulkEditOptionTitle: {
    color: theme.text,
    fontSize: '15px',
    fontWeight: '500',
  },
  bulkEditOptionDesc: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  bulkEditCancelBtn: {
    width: '100%',
    padding: '14px',
    marginTop: '16px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.border}`,
    borderRadius: '12px',
    color: theme.textSecondary,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  // Card status badges
  cardBadge: {
    position: 'absolute',
    padding: '3px 6px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.text,
    fontWeight: '600',
    fontSize: '10px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  },
  cardBadgeSmall: {
    padding: '2px 4px',
    fontSize: '8px',
    borderRadius: '3px',
  },
  cardBadgeBottomLeft: {
    bottom: '4px',
    left: '4px',
  },
  cardBadgeTopRight: {
    top: '4px',
    right: '4px',
  },
  cardBadgeTopLeft: {
    top: '4px',
    left: '4px',
  },
  cardBadgeBottomRight: {
    bottom: '4px',
    right: '4px',
  },
  cardBadgeText: {
    lineHeight: 1,
  },
  // Selection indicator on cards
  cardItemSelected: {
    outline: `2px solid ${theme.accent}`,
    outlineOffset: '-2px',
    borderRadius: '10px',
  },
  selectionIndicator: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    width: '22px',
    height: '22px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    border: `2px solid ${theme.text}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  selectionIndicatorActive: {
    backgroundColor: theme.accent,
    borderColor: theme.accent,
    color: theme.text,
  },
  // Floating action bar
  bulkEditActionBar: {
    position: 'fixed',
    bottom: '90px',
    left: '12px',
    right: '12px',
    backgroundColor: theme.bgCard,
    borderRadius: '16px',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
    border: `1px solid ${theme.border}`,
    zIndex: 100,
  },
  bulkEditCancelAction: {
    padding: '10px 16px',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.border}`,
    borderRadius: '10px',
    color: theme.textSecondary,
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  bulkEditActionInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2px',
  },
  bulkEditActionLabel: {
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
  },
  bulkEditActionCount: {
    color: theme.textSecondary,
    fontSize: '12px',
  },
  bulkEditApplyAction: {
    padding: '10px 20px',
    backgroundColor: theme.accent,
    border: 'none',
    borderRadius: '10px',
    color: theme.text,
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },

  // Smart Search Results Panel Styles
  searchResultsPanel: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'calc(env(safe-area-inset-top) + 145px)', // Below header + tabs + toolbar
  },
  searchResultsBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.overlay,
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  searchResultsContainer: {
    position: 'relative',
    margin: '8px 12px',
    maxHeight: 'calc(100vh - env(safe-area-inset-top) - 230px)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  searchSection: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  searchSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px',
    backgroundColor: theme.bgSheet,
    borderRadius: '14px',
    border: '1px solid',
    borderColor: theme.border,
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  searchSectionLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  searchSectionTitle: {
    fontSize: '15px',
    fontWeight: '600',
  },
  searchSectionRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: theme.textSecondary,
  },
  searchSectionCount: {
    padding: '4px 10px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '700',
    minWidth: '28px',
    textAlign: 'center',
  },
  searchSectionSpinner: {
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: `2px solid ${theme.border}`,
    borderTopColor: theme.accent,
  },
  searchSectionContent: {
    overflow: 'hidden',
    marginTop: '8px',
    backgroundColor: theme.bgCard,
    borderRadius: '14px',
    border: `1px solid ${theme.border}`,
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.1)',
  },
  searchSectionScrollable: {
    maxHeight: '280px',
    overflowY: 'auto',
    padding: '12px',
    WebkitOverflowScrolling: 'touch',
  },
  searchCardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '10px',
  },
  searchCardItem: {
    position: 'relative',
    aspectRatio: '0.714',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: theme.bg,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
  },
  searchCardImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  searchCardBadge: {
    position: 'absolute',
    bottom: '4px',
    right: '4px',
    width: '18px',
    height: '18px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: '700',
    color: '#ffffff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
  },
  searchCardStatus: {
    position: 'absolute',
    bottom: '4px',
    right: '4px',
    display: 'flex',
    gap: '2px',
  },
  searchCardBadgeSmall: {
    width: '16px',
    height: '16px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '9px',
    fontWeight: '700',
    color: '#ffffff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
  },
  searchSetItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    backgroundColor: theme.bgSheet,
    borderRadius: '10px',
    cursor: 'pointer',
    marginBottom: '6px',
  },
  searchSetLogo: {
    height: '28px',
    width: 'auto',
    objectFit: 'contain',
  },
  searchSetName: {
    fontSize: '13px',
    fontWeight: '500',
    color: theme.text,
    flex: 1,
  },
  searchSeeMore: {
    marginTop: '8px',
    textAlign: 'center',
    padding: '8px',
    backgroundColor: theme.bgSheet,
    borderRadius: '8px',
  },
  searchClearButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    backgroundColor: theme.textSecondary,
    border: 'none',
    cursor: 'pointer',
    color: theme.bgSheet,
    flexShrink: 0,
    padding: 0,
  },
});

// Add keyframes for animations
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes matchPulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 0 20px rgba(250, 204, 21, 0.3);
    }
    50% {
      transform: scale(1.05);
      box-shadow: 0 0 30px rgba(250, 204, 21, 0.5);
    }
  }
  @keyframes fadeSlideIn {
    from {
      opacity: 0;
      transform: translateX(10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;
if (!document.querySelector('style[data-collection-animations]')) {
  styleSheet.setAttribute('data-collection-animations', 'true');
  document.head.appendChild(styleSheet);
}

export default Collection;
